import type { LocaleBundle } from '../../types.ts';

const bundle: LocaleBundle = {
  'actions.cancel': 'Cancel',
  'actions.save': 'Save',
  'actions.save-changes': 'Save Changes',
  'actions.saving': 'Saving...',
  'actions.delete': 'Delete',
  'actions.submit': 'Submit',
  'actions.remove': 'Remove',
  'common.loading': 'Loading...',
  'errors.auth.missing-header': 'Missing or invalid authorization header',
  'errors.auth.invalid-token': 'Invalid access token',
  'errors.auth.token-validation-failed': 'Failed to validate access token',
  'email.login-link.subject': 'Your Goalpost Login Link',
  'email.welcome.subject': 'Welcome to Goalpost! 🎉',
  'email.passkey-added.subject': 'A passkey was added to your Goalpost account',
  'email.change-batch-approved.subject': '{$document} Changes',
  'email.change-batch-submitted.subject': '{$document}: change batch submitted for approval',
  'email.change-batch-rejected.subject': '{$document}: your change batch was rejected',
  'email.partial-approval.subject': '{$document}: your change batch received an approval',
  'email.invitation.workspace.subject': '{$inviter} invited you to {$workspace} on Goalpost',
  'email.invitation.team.subject': '{$inviter} invited you to join {$team} on Goalpost',
  // Detail-tree operation labels rendered in change-batch-approved emails by
  // getOperationDescription (app/src/email-helpers.ts). ADD / REPLACE always
  // carry content (the operation schema requires it), so they only have a
  // `*.with-content` form; REMOVE / MOVE resolve content from the nullable
  // details.content column, so they keep a bare fallback for empty/legacy
  // rows.
  //
  // These name the action in full. The plain-text email renders them inline;
  // the HTML email pairs an icon with the operation's content instead and hangs
  // this phrasing off the icon as its accessible name and hover text.
  'detail-tree.added.with-content': 'Added detail: {$content}',
  'detail-tree.removed': 'Removed a detail',
  'detail-tree.removed.with-content': 'Removed: "{$content}"',
  'detail-tree.replaced.with-content': 'Replaced detail: {$content}',
  'detail-tree.moved': 'Moved a detail',
  'detail-tree.moved.with-content': 'Moved: "{$content}"',
  'detail-tree.label-added': 'Added a label',
  'detail-tree.label-added.with-content': 'Added a label to: "{$content}"',
  'detail-tree.label-removed': 'Removed a label',
  'detail-tree.label-removed.with-content': 'Removed a label from: "{$content}"',
  'detail-tree.rule-set': 'Set an approval rule',
  'detail-tree.rule-set.with-content': 'Set an approval rule on: "{$content}"',
  'detail-tree.rule-cleared': 'Cleared an approval rule',
  'detail-tree.rule-cleared.with-content': 'Cleared the approval rule on: "{$content}"',
  'detail-tree.unknown': 'Unknown operation: {$operationType}',
  // User reference surface page titles. These render in the browser tab
  // for /docs/* pages and are translated independently of the page body
  // (which is structured JSX inline in the component).
  'docs.getting-started.title': 'Getting Started — Goalpost docs',
  'docs.workspace.title': 'Workspace — Goalpost docs',
  'docs.documents.title': 'Documents — Goalpost docs',
  'docs.change-management.title': 'Change Management — Goalpost docs',
  'docs.approval-rules.title': 'Approval Rules — Goalpost docs',
  'docs.details.title': 'Editing a Document — Goalpost docs',
  'docs.teams.title': 'Teams — Goalpost docs',
  'docs.ai-integration.title': 'AI Integrations — Goalpost docs',
  'docs.getting-started.label': 'Getting Started',
  'docs.getting-started.desc': 'Sign up, start your first document, and submit your first change.',
  'docs.workspace.label': 'Workspace',
  'docs.workspace.desc': 'Your account, documents, people, and billing in one place.',
  'docs.documents.label': 'Documents',
  'docs.documents.desc': 'Create documents, share them, and control who can see and change them.',
  'docs.change-management.label': 'Change Management',
  'docs.change-management.desc': 'How a batch of changes moves from a working draft to an approved version.',
  'docs.approval-rules.label': 'Approval Rules',
  'docs.approval-rules.desc': 'Decide who must sign off before a change becomes official.',
  'docs.details.label': 'Editing a Document',
  'docs.details.desc': 'Add, move, label, and organize the contents of the document.',
  'docs.teams.label': 'Teams',
  'docs.teams.desc': 'Group people to grant document access and organize approvals.',
  'docs.ai-integration.label': 'AI Integrations',
  'docs.ai-integration.desc': 'Connect an AI assistant to read your documents and draft changes via MCP.',
  // Repeated label on cross-link affordances out of the product into
  // /docs/*. Kept generic so the same string can appear in multiple
  // contexts without per-site rewrites.
  'docs.learn-more-arrow': 'Learn more →',
  // One-line concept definitions, matching how the docs introduce each
  // concept, used as the header description on the matching product page.
  'docs.documents.summary': 'A document is a structured list of lists that holds your notes, context, reference materials, requirements, and decisions, along with links and images, organized with sections and labels.',
  'docs.teams.summary': 'A team is a named group of people in your workspace. Teams are how you grant document access and how approvals are organized.',
  // Dashboard page. `welcome.named` renders for signed-in users with a
  // display name; the bare `welcome` is the fallback when no name is
  // available (anonymous greeting, missing profile field).
  'dashboard.welcome': 'Welcome!',
  'dashboard.welcome.named': 'Welcome, {$name}!',
  'dashboard.see-all': 'See all',
  'dashboard.pending-approvals.heading': 'Approval Requests',
  'dashboard.pending-approvals.see-all-aria': 'See all pending approvals',
  'dashboard.pending-approvals.error': 'Failed to load pending actions',
  'dashboard.pending-approvals.empty': 'No pending actions. You are all caught up.',
  'dashboard.in-progress.heading': 'In-Progress Changes',
  'dashboard.in-progress.error': 'Failed to load in-progress change batches',
  'dashboard.in-progress.empty': 'No drafts or pending change batches. Start a new draft from any document.',
  'dashboard.documents.heading': 'Documents',
  'dashboard.documents.see-all-aria': 'See all documents',
  'dashboard.documents.error': 'Failed to load documents',
  'dashboard.documents.create-first': 'Create your first document',
  'dashboard.column.batch': 'Batch',
  'dashboard.column.status': 'Status',
  'dashboard.column.submitted': 'Submitted',
  'dashboard.column.created': 'Created',
  'dashboard.column.document': 'Document',
  'dashboard.column.updated': 'Updated',
  // Dashboard "Learn" aside. Reference voice — each summary describes
  // what the linked /docs/* page explains, not why the reader should
  // care. See docs/user-education.md: the dashboard is a Decide
  // surface; the aside bridges into the Understand surface.
  'learn-aside.heading': 'Learn',
  'learn-aside.more': 'Read the docs →',
  'learn-aside.workspace.title': 'Workspace',
  'learn-aside.workspace.summary': 'How Goalpost fits together — workspaces, teams, documents, and the approval rules that connect them.',
  'learn-aside.approvals.title': 'Approval rules',
  'learn-aside.approvals.summary': 'Rule types, two-layer evaluation, lifecycle, and how a rule snapshot preserves the policy in force at submission.',
  'learn-aside.ai.title': 'AI integrations',
  'learn-aside.ai.summary': 'How MCP-compatible assistants like Claude and Cursor connect to draft change batches against your documents.',
  // Guided setup onboarding checklist (Act 1). See docs/user-education.md.
  // Step bodies are one-line summaries sourced from the /docs/* reference ledes.
  'onboarding.title': 'Set up your workspace',
  'onboarding.progress': '{$step} of {$total}',
  'onboarding.dismiss': 'Dismiss',
  'onboarding.skip': 'Skip',
  'onboarding.back': 'Back',
  // Appended after a step's nav link once the user is already on that page, so
  // the checklist reads "you're here — now do the next part".
  'onboarding.nav-done': 'Done',
  'onboarding.step.team.title': 'Set up your team',
  'onboarding.step.team.body': 'You have a team named after you ({$team}). This is a good team name when getting started or working solo. Rename it now, or anytime, to make it your own. Teams are how you grant document access.',
  'onboarding.step.team.cta': 'Rename your team',
  // Each step body is why + how: what the step accomplishes, then the path to
  // do it. The {#link} wraps the destination the banner turns into a live route
  // link — the card's only navigation affordance (there is no CTA button, so
  // nothing competes with the page's own primary action).
  'onboarding.step.document.title': 'Create a document',
  'onboarding.step.document.body': '{#p}To get started, you need to create a document where your notes, links, and decisions will live.{/p}{#p}To create a document:{/p}{#ol}{#navstep}Navigate to the {#link}New Document{/link} page.{/navstep}{#li}Fill in your document name and optional description.{/li}{#li}Click the {#strong}Create Document{/strong} button.{/li}{/ol}',
  'onboarding.step.draft.title': 'Create a change batch',
  'onboarding.step.draft.body': '{#p}Changes are grouped into a batch so they\'re drafted, reviewed, and approved together.{/p}{#p}To start a change batch:{/p}{#ol}{#navstep}Navigate to {#link}Propose Changes{/link}.{/navstep}{#li}Give the batch a name and optional description.{/li}{#li}Click the {#strong}Create Draft{/strong} button.{/li}{/ol}',
  'onboarding.step.detail.title': 'Add a detail',
  'onboarding.step.detail.body': '{#p}A document\'s content lives in sections.{/p}{#p}To add your first item:{/p}{#ol}{#navstep}Open {#link}the batch{/link}.{/navstep}{#li}Click {#strong}Add section{/strong} and name it, then click {#strong}Add detail{/strong} inside the section.{/li}{#li}Enter your text and click {#strong}Save{/strong}.{/li}{/ol}',
  'onboarding.step.submit.title': 'Approve the changes',
  'onboarding.step.submit.body': '{#p}Approving finalizes the batch into an official version — assigning a version number, notifying the people who need to know, and updating the document overview. With no approval rule yet, it approves immediately.{/p}{#p}To approve the changes:{/p}{#ol}{#navstep}Open {#link}the batch{/link}.{/navstep}{#li}Click the {#strong}Approve Changes{/strong} button.{/li}{/ol}',
  'onboarding.celebrate.title': 'You\'re all set!',
  'onboarding.celebrate.body': 'You\'ve taken a change from draft all the way to approved — that\'s the core Goalpost loop. Whenever you\'re ready, you can refine your goals. Make a new batch, add details with greater specificity, review, approve, repeat.',
  'onboarding.celebrate.dismiss': 'Done',
  // Document-level approval rule labels (radio option text) and
  // descriptions (helper text under each option, and the rendered
  // summary on the active rule). Description templates use MF2
  // `{$placeholder}` variables and selectors, formatted at the call
  // site in webapp/src/utils/describe-rule.ts.
  // Team-level approval rule labels and descriptions, rendered as
  // radio options on the team Approval tab.
  'approval.team-rule.any_one.label': 'Any one member',
  'approval.team-rule.any_one.description': 'A single team member approval is sufficient',
  'approval.team-rule.all.label': 'All members',
  'approval.team-rule.all.description': 'Every team member must approve',
  'approval.team-rule.at_least_n.label': 'Minimum number of team members',
  'approval.team-rule.at_least_n.description': 'Configure the minimum number of members who must approve.',
  'approval.team-rule.unknown.description': 'Unknown approval rule',
  // Team layout / secondary navigation tab labels for
  // /workspace/:workspaceId/teams/:teamId/* pages.
  'team-nav.members': 'Members',
  'team-nav.invites': 'Invites',
  'team-nav.documents': 'Documents',
  'team-nav.approval': 'Approval',
  'team-nav.settings': 'Settings',
  // Empty / fallback states for the team layout shell when the team or
  // permissions are missing.
  'team-layout.no-access.title': 'Team',
  'team-layout.no-access.description': 'Only workspace owners can manage teams.',
  'team-layout.loading-team': 'Loading team...',
  'team-layout.not-found': 'Team not found',
  // Team Settings tab (TeamSettings.tsx). Page titles are concatenated
  // with the team name at the call site as `${team.name} - <suffix>`;
  // the suffix lives in `team-settings.page-title-suffix` and matches
  // the suffix convention used elsewhere.
  'team-settings.page-title-suffix': 'Settings',
  'team-settings.name.heading': 'Team Name',
  'team-settings.name.label': 'Name',
  'team-settings.name.saved': 'Team name saved',
  'team-settings.name.error': 'Failed to update team name',
  'team-settings.archive.heading': 'Archive Team',
  'team-settings.archive.badge': 'Archived',
  'team-settings.archive.description': 'Archiving retires a team from new use. It stays visible in approval history and everywhere it is already referenced, but it can no longer be added to an approval rule or assigned to a new document. A team must be removed from all its documents and approval rules before it can be archived.',
  'team-settings.archive.description-archived': 'This team is archived. It stays resolvable wherever it is already referenced, but is hidden from assignment and approval-rule pickers. Unarchive it to make it available again.',
  'team-settings.archive.button': 'Archive Team',
  'team-settings.archive.unarchive-button': 'Unarchive Team',
  'team-settings.archive.archived': 'Team archived',
  'team-settings.archive.unarchived': 'Team unarchived',
  'team-settings.archive.error': 'Failed to update the team’s archived state',
  'teams.archived-badge': 'Archived',
  'team-settings.delete.heading': 'Delete Team',
  'team-settings.delete.description': 'Deleting a team is permanent.',
  'team-settings.delete.button': 'Delete Team',
  'team-settings.delete.confirm.title': 'Delete this team',
  'team-settings.delete.confirm.message': 'This team and all its memberships will be permanently removed. This action cannot be undone.',
  'team-settings.delete.confirm.action': 'Delete Team',
  'team-settings.delete.error': 'Failed to delete team',
  // Team Members tab (TeamMembers.tsx).
  'team-members.page-title-suffix': 'Members',
  'team-members.description': 'Team members can access documents assigned to this team. Add an existing workspace member to grant them access.',
  'team-members.column.member': 'Member',
  'team-members.column.email': 'Email',
  'team-members.column.actions-aria': 'Actions',
  'team-members.select-placeholder': 'Select a member to add...',
  'team-members.add.button': 'Add Member',
  'team-members.add.busy': 'Adding...',
  'team-members.add.error': 'Failed to add member',
  'team-members.remove.confirm.title': 'Remove team member',
  'team-members.remove.confirm.message': 'This member will lose access to team resources.',
  'team-members.remove.confirm.action': 'Remove Member',
  'team-members.remove.error': 'Failed to remove member',
  // Team Approval tab (TeamApproval.tsx).
  'team-approval.page-title-suffix': 'Approval',
  'team-approval.heading': 'Approval Rule',
  'team-approval.description': "How many members must approve for this team's approval to count.",
  'team-approval.rule-fieldset-aria': 'Rule',
  'team-approval.n.label': 'Minimum approvals needed (N)',
  'team-approval.save.button': 'Save Rule',
  'team-approval.save.error': 'Failed to update approval rule',
  // Team Invites tab (TeamInvites.tsx). `used-prefix` is concatenated
  // with the formatted date at the call site (rendered shape:
  // `<prefix> <date>`), following the prefix convention used by
  // detail-tree.* above.
  'team-invites.page-title-suffix': 'Invites',
  'team-invites.heading': 'Invitation Codes',
  'team-invites.description': 'Generate invitation codes to invite users directly to this team. They will be added to the workspace as a member and to this team.',
  'team-invites.column.code': 'Code',
  'team-invites.column.created': 'Created',
  'team-invites.column.expires': 'Expires',
  'team-invites.column.status': 'Status',
  'team-invites.column.actions-aria': 'Actions',
  'team-invites.used-prefix': 'Used',
  'team-invites.status.used': 'Used',
  'team-invites.status.active': 'Active',
  'team-invites.status.expired': 'Expired',
  'team-invites.copy.button': 'Copy Link',
  'team-invites.copy.done': 'Copied!',
  'team-invites.copy.fallback-prompt': 'Copy invitation link:',
  'team-invites.revoke.button': 'Revoke',
  'team-invites.revoke.busy': 'Revoking...',
  'team-invites.revoke.error': 'Failed to revoke invitation code',
  'team-invites.generate.button': 'Generate Invitation Code',
  'team-invites.generate.busy': 'Generating Code...',
  'team-invites.generate.error': 'Failed to generate invitation code',
  // Team Documents tab (TeamDocuments.tsx). `assign.success` is
  // formatted with the document name (or the `unknown-document-fallback`
  // when the document record is missing). `remove.*` messages with
  // `{$document}` are formatted at the call site.
  'team-documents.page-title-suffix': 'Documents',
  'team-documents.column.document': 'Document',
  'team-documents.column.actions-aria': 'Actions',
  'team-documents.select-placeholder': 'Select a document...',
  'team-documents.assign.button': 'Assign',
  'team-documents.assign.busy': 'Assigning...',
  'team-documents.assign.success': 'Team assigned to {$document}',
  'team-documents.assign.unknown-document-fallback': 'document',
  'team-documents.assign.error': 'Failed to assign team to document',
  'team-documents.assign.help-text': 'Every member of this team will see the whole document and can propose changes to it.',
  'team-documents.remove.confirm.title': 'Remove from document',
  'team-documents.remove.confirm.message': 'This team will no longer have access to "{$document}".',
  'team-documents.remove.confirm.action': 'Remove from Document',
  'team-documents.remove.in-use-error': "Cannot remove team from \"{$document}\" — it is part of the document's approval rule. Update the approval rule first.",
  'team-documents.remove.error': 'Failed to remove team from document',
  // Common UI strings shared across pages.
  'common.error': 'Error',
  // Auth: login page form labels, validation, and button states.
  'login.heading': 'Login',
  'login.email.label': 'Email',
  'login.continue': 'Continue',
  'login.continue.submitting': 'One moment...',
  'login.signing-in-as': 'Signing in as {$email}.',
  'login.change-email': 'Not you?',
  'login.password.label': 'Password',
  'login.password.submit': 'Log in',
  'login.password.submitting': 'Signing in...',
  'login.magic-link.submit': 'Email me a login link',
  'login.magic-link.sending': 'Sending...',
  'login.magic-link.sent': 'Login link sent to {$email}! Check your email and click the link to sign in. The link will expire in {$minutes} minutes.',
  'login.register-prompt': "Don't have an account?",
  'login.register-link': 'Register',
  'login.errors.sign-in-failed': 'Sign-in failed',
  'login.errors.send-link-failed': 'Failed to send login link',
  // Passkey sign-in on /login and the OAuth authorize page: offered in the
  // email field's autofill, and by a button below the form.
  'login.passkey.submit': 'Sign in with a passkey',
  'login.passkey.submitting': 'Waiting for your passkey...',
  'login.errors.passkey-failed': "That passkey didn't work. Try again, or sign in another way.",
  // Auth: where the links in sign-in emails land. The token is redeemed only
  // when the person clicks, so the page needs a button and a reason for it.
  'email-link.heading': 'Finish signing in',
  'email-link.registration.heading': 'Finish creating your account',
  'email-link.body': 'You opened a sign-in link from your email. Continue to sign in to Goalpost.',
  'email-link.submit': 'Continue to Goalpost',
  'email-link.submitting': 'Signing in...',
  'email-link.errors.invalid': 'This sign-in link is invalid or has expired. Links can only be used once and expire after 15 minutes.',
  'email-link.errors.failed': 'Sign-in failed. Please try again.',
  'email-link.request-new': 'Request a new link',
  // Auth: register page (shared with RegisterWithInvitation).
  'register.heading': 'Register',
  'register.mode.password': 'Password',
  'register.mode.magic-link': 'Email Link',
  'register.email.label': 'Email',
  'register.email.placeholder': 'Enter your email address',
  'register.display-name.label': 'Your Name',
  'register.display-name.placeholder': 'Optional',
  'register.password.label': 'Password',
  'register.password.submit': 'Register',
  'register.password.submitting': 'Creating account...',
  'register.confirm-password.label': 'Confirm Password',
  'register.magic-link.submit': 'Register',
  'register.magic-link.sending': 'Sending link...',
  'register.magic-link.sent': 'Registration link sent to {$email}! Check your email and click the link to complete your registration. The link will expire in {$minutes} minutes.',
  'register.login-prompt': 'Already have an account?',
  'register.login-link': 'Login',
  'register.validation.passwords-do-not-match': 'Passwords do not match',
  'register.validation.password-too-short': 'Password must be at least 8 characters',
  'register.validation.invalid-email': 'Please enter a valid email address',
  'register.errors.registration-failed': 'Registration failed',
  'register-invitation.heading': 'Register to Accept Invitation',
  'register-invitation.description': 'Create an account to accept your invitation.',
  'register-invitation.email-locked': 'This invitation was sent to {$email}. Register with that address to accept it.',
  'register-invitation.magic-link.sent': 'Registration link sent to {$email}! Check your email and click the link to sign in. The link will expire in {$minutes} minutes.',
  // Request access page (alpha waitlist signup).
  'request-access.heading': 'Request access',
  'request-access.email.label': 'Email',
  'request-access.email.placeholder': 'you@example.com',
  'request-access.display-name.label': 'Your Name',
  'request-access.display-name.placeholder': 'Optional',
  'request-access.submit': 'Request access',
  'request-access.submitting': 'Submitting...',
  'request-access.login-prompt': 'Already have an invitation?',
  'request-access.login-link': 'Login',
  'request-access.invited-prompt': 'Already invited?',
  'request-access.invited-link': 'Create your account',
  'request-access.validation.invalid-email': 'Please enter a valid email address',
  'request-access.validation.captcha-required': 'Please complete the captcha',
  'request-access.errors.submit-failed': 'Could not submit request',
  // Create-team form (CreateTeamForm.tsx).
  'create-team.name.label': 'Team Name *',
  'create-team.name.placeholder': 'Enter team name',
  // Character-count below maxLength-limited fields. `{$count}` is the
  // current input length; the catalog template carries the maximum
  // verbatim so different fields don't need separate keys for the
  // ".../255 characters" suffix.
  'create-team.name.character-count': '{$count}/255 characters',
  'create-team.submit': 'Create Team',
  'create-team.submitting': 'Creating...',
  'create-team.validation.name-required': 'Team name is required',
  'create-team.validation.name-too-long': 'Team name must be 255 characters or less',
  'create-team.errors.create-failed': 'Failed to create team',
  // Create-document form (CreateDocumentForm.tsx).
  'create-document.name.label': 'Document Name *',
  'create-document.name.placeholder': 'Enter document name',
  'create-document.name.character-count': '{$count}/100 characters',
  'create-document.description.label': 'Description',
  'create-document.description.placeholder': 'Describe your document',
  'create-document.description.character-count': '{$count}/500 characters',
  'create-document.teams.legend': 'Teams',
  'create-document.teams.help': "Teams assigned to this document can view and propose changes. Every member of a selected team will see the whole document.",
  'create-document.teams.empty': 'This workspace has no teams. Documents are reached through teams, so create a team before creating a document.',
  'create-document.submit': 'Create Document',
  'create-document.submitting': 'Creating...',
  'create-document.validation.name-required': 'Document name is required',
  'create-document.validation.name-too-short': 'Document name must be at least 2 characters',
  'create-document.validation.name-too-long': 'Document name must be 100 characters or less',
  'create-document.validation.description-too-long': 'Description must be 500 characters or less',
  'create-document.validation.team-required': 'Select at least one team',
  'create-document.errors.create-failed': 'Failed to create document',
  // Create-workspace page and form. Reached from the dashboard CTA shown
  // to invited members who don't yet own a workspace of their own.
  'create-workspace.heading': 'Create your own workspace',
  'create-workspace.description': 'Start a personal workspace for your own documents. You\'ll be the owner.',
  'create-workspace.name.label': 'Workspace Name *',
  'create-workspace.name.placeholder': 'Enter workspace name',
  'create-workspace.name.character-count': '{$count}/100 characters',
  'create-workspace.description.label': 'Description',
  'create-workspace.description.placeholder': 'Describe your workspace',
  'create-workspace.description.character-count': '{$count}/500 characters',
  'create-workspace.submit': 'Create Workspace',
  'create-workspace.submitting': 'Creating...',
  'create-workspace.validation.name-required': 'Workspace name is required',
  'create-workspace.validation.name-too-short': 'Workspace name must be at least 2 characters',
  'create-workspace.validation.name-too-long': 'Workspace name must be 100 characters or less',
  'create-workspace.validation.description-too-long': 'Description must be 500 characters or less',
  'create-workspace.errors.create-failed': 'Failed to create workspace',
  // Dashboard CTA: shown to members who don't own any workspace yet.
  'dashboard.create-workspace.cta': 'Create your own workspace',
  // Create-draft form (CreateDraftForm.tsx).
  'create-draft.title.label': 'Batch name *',
  'create-draft.title.character-count': '{$count}/100 characters',
  'create-draft.description.label': 'Description',
  'create-draft.description.character-count': '{$count}/500 characters',
  'create-draft.submit': 'Create Draft',
  'create-draft.submitting': 'Creating...',
  'create-draft.validation.title-required': 'Title is required',
  'create-draft.validation.title-too-short': 'Title must be at least 2 characters',
  'create-draft.validation.title-too-long': 'Title must be 100 characters or less',
  'create-draft.validation.description-too-long': 'Description must be 500 characters or less',
  // Preview banner label rendered above the CreateDraftForm when the user
  // arrived here via an inline detail edit. The op description itself is
  // generated by getDetailOperationDescriptionJSX (the same util the
  // Changes tab uses), so only the label needs an i18n key.
  'create-draft.pending-change.label': 'Pending change',
  // Root add form (AddRootDetailForm.tsx): roots are sections.
  'section.add.trigger': 'Add section',
  'section.add.placeholder': 'Name the new section...',
  'section.add.content-aria': 'Section title',
  // How a section row announces its own role (DetailCard.tsx), since nothing
  // else tells a screen reader that a row is a grouping rather than a detail.
  'detail.section': 'Section',
  // The section navigator beside the detail views (SectionNav.tsx).
  'section-nav.aria': 'Sections',
  'section-nav.all': 'All sections',
  // The button that folds the navigator to a rail, and the rail's button
  // that unfolds it.
  'section-nav.collapse': 'Hide sections',
  'section-nav.expand': 'Show sections',
  'section-nav.empty': 'Sections you add will be listed here.',
  // The "?" beside "All sections" (HelpPopover.tsx). The copy is split
  // around the inline PlusIcon so the icon stays inline; the suffix
  // concatenates after the icon mid-sentence.
  'section-nav.help.aria': 'About sections and details',
  'section-nav.help.before-icon': 'Sections group the content of this document. Pick a section to focus on it. You can edit while the batch is a draft, but not when it is pending approval or approved. Add a detail inside a section by clicking the',
  'section-nav.help.after-icon': 'next to its parent.',
  'section-nav.add-section': 'Add section',
  'section-nav.add-subsection': 'Add sub-section',
  'section-nav.rename': 'Rename',
  'section-nav.remove': 'Remove',
  'section-nav.rename-placeholder': 'Section title...',
  // The heading above the specification, naming the section in view and the
  // path back out to the whole tree (SectionBreadcrumb.tsx).
  'section-breadcrumb.aria': 'Section path',
  // The kind switcher at the top of the add-child editor (DetailCard.tsx).
  'detail.add-child.type-aria': 'Kind of detail',
  'detail.add-child.type.text': 'Text',
  'detail.add-child.type.section': 'Sub-section',
  'detail.add-child.type.link': 'Link',
  'detail.add-child.type.ref': 'Reference',
  'detail.add-child.type.image': 'Image',
  'detail.add-subsection.placeholder': 'Name the new sub-section...',
  // Link details: the URL + label editor fields, and the accessible name of
  // the link glyph on a link row (DetailCard.tsx).
  'detail.link': 'Link',
  'detail.link.url-placeholder': 'https://...',
  'detail.link.url-aria': 'Link URL',
  'detail.link.label-placeholder': 'Label (optional)',
  'detail.link.label-aria': 'Link label',
  // Search box above the detail views (DetailSearch.tsx).
  'detail-search.placeholder': 'Find in document...',
  'detail-search.aria': 'Find details',
  'detail-search.clear': 'Clear search',
  // Reference details: the target picker, and the row's glyph and states
  // (DetailCard.tsx, DetailPicker.tsx).
  'detail.ref': 'Reference',
  'detail.ref.go-to': 'Go to the referenced detail',
  'detail.ref.missing': '(referenced detail is not in this version)',
  'detail.ref.chosen': 'Points to:',
  'detail.ref.picker-placeholder': 'Search for the detail to reference...',
  'detail.ref.picker-aria': 'Reference target',
  // Image details: the alt-text field and the row's states (DetailCard.tsx,
  // AssetImage.tsx).
  'detail.image': 'Image',
  'detail.image.alt-placeholder': 'Describe the image (required)...',
  'detail.image.alt-aria': 'Image description',
  'detail.image.loading': 'Loading image...',
  'detail.image.unavailable': 'Image unavailable',
  'detail.image.unsupported-type': 'Choose a PNG, JPEG, GIF, or WebP image.',
  'detail.image.too-large': 'Images must be 5 MB or smaller.',
  'detail.image.upload-failed': 'The image could not be uploaded.',
  // Detail add/edit affordances (AddRootDetailForm.tsx, DetailCards.tsx).
  'detail.add.trigger': 'Add detail',
  'detail.add.placeholder': 'Enter your new detail...',
  'detail.add.content-aria': 'Detail content',
  'detail.add-child.placeholder': 'Enter your new detail...',
  'detail.content.empty': '(empty)',
  'detail.previous-content-aria': 'Previous content',
  'detail.actions.add-child': 'Add child detail',
  'detail.actions.edit': 'Edit detail',
  'detail.actions.remove': 'Remove detail',
  'detail.actions.drag': 'Drag to reorder',
  'detail.add-sibling.placeholder': 'Enter a sibling detail...',
  // Label chips on a detail row and the inline label editor (DetailCard.tsx).
  'detail.actions.labels': 'Edit labels',
  // Governance (approval rule) affordance on a detail row and its inline
  // picker (DetailCard.tsx). A ref on a section root governs its whole subtree.
  'detail.actions.rule': 'Set approval rule',
  'detail.governance.governed-by': 'Requires approval: {$name}',
  'detail.governance.inherited': 'Requires: {$name}',
  'detail.governance.inherited-from': 'Inherited from an ancestor: {$name}',
  'detail.governance.editor-label': 'Approval rule for this detail',
  'detail.governance.none': 'None',
  'detail.governance.hint': 'Governs this detail and everything under it · the rule’s teams must be assigned to the document',
  'detail.governance.inherited-note': 'This detail is still covered by an ancestor’s rule: {$name}',
  'detail.governance.save': 'Save',
  'detail.labels.input-placeholder': 'Add a label...',
  'detail.labels.input-aria': 'New label',
  'detail.labels.add': 'Add',
  'detail.labels.save': 'Save',
  'detail.labels.remove-aria': 'Remove label {$label}',
  // Shown to users who can only select existing labels (label creation is a
  // workspace-owner capability); label creators get no hint — the placeholder
  // and Save/Cancel buttons carry the affordance.
  'detail.labels.select-only-hint': 'Pick from existing labels · New labels are created by workspace owners',
  // Inline errors when typed input can't become a chip — the text stays in
  // the input so nothing is silently discarded.
  'detail.labels.error.select-only': 'No matching label — new labels are created by workspace owners',
  'detail.labels.error.cap': 'A detail can carry at most 20 labels',
  // Label filter bar above the detail views. Toggling label chips narrows
  // every view to details carrying at least one selected label (plus their
  // ancestors, for orientation). The empty copy also covers the search box,
  // which narrows the views the same way.
  'detail-filter.heading': 'Filter by label',
  'detail-filter.clear': 'Clear filter',
  'detail-filter.chip-aria': 'Filter by label {$label}',
  'detail-filter.empty': 'No details match the current filter.',
  // The toolbar toggle that swaps the specification for the list of what
  // the version changed; sits beside the tree's expand/collapse controls.
  'detail-filter.changes-only': 'Changes only',
  // Inline hint under every detail-content editor. Reinforces the
  // Enter-saves / Shift+Enter-newline convention shared across editors.
  // Keyboard shortcuts help overlay (the `?` palette) and the sidebar entry
  // that opens it. Groups mirror SHORTCUT_GROUPS in webapp keyboard-shortcuts.ts.
  'shortcuts.title': 'Keyboard shortcuts',
  'shortcuts.close': 'Close keyboard shortcuts',
  'shortcuts.group.global': 'Global',
  'shortcuts.group.document': 'Document navigation',
  'shortcuts.group.reorder': 'Reordering a detail',
  'shortcuts.group.edit': 'Editing a detail',
  'shortcuts.group.editing-text': 'While editing text',
  'shortcuts.group.draft': 'Draft history',
  'shortcuts.global.help': 'Show or hide this help',
  'shortcuts.global.focus-nav': 'Focus the navigation sidebar',
  'shortcuts.global.focus-main': 'Focus the main content',
  'shortcuts.document.move': 'Move between details',
  'shortcuts.document.expand': 'Expand, or go to first child',
  'shortcuts.document.collapse': 'Collapse, or go to parent',
  'shortcuts.document.ends': 'Jump to first / last detail',
  'shortcuts.document.toggle': 'Expand or collapse',
  'shortcuts.reorder.up': 'Move up among siblings',
  'shortcuts.reorder.down': 'Move down among siblings',
  'shortcuts.reorder.indent': 'Indent under previous sibling',
  'shortcuts.reorder.outdent': 'Outdent to parent level',
  'shortcuts.edit.edit': 'Edit this detail',
  'shortcuts.edit.add-child': 'Add a child detail',
  'shortcuts.edit.add-sibling': 'Add a sibling detail',
  'shortcuts.edit.labels': 'Edit this detail\'s labels',
  'shortcuts.edit.rule': 'Set this detail\'s approval rule',
  'shortcuts.edit.remove': 'Remove this detail',
  'shortcuts.editing-text.save': 'Save',
  'shortcuts.editing-text.newline': 'New line',
  'shortcuts.editing-text.cancel': 'Cancel',
  'shortcuts.draft.undo': 'Undo the last save',
  'shortcuts.draft.redo': 'Redo the undone save',
  // Change Batch controls.
  'change-batch.delete-draft': 'Delete Draft',
  // DocumentHeader inline-edit affordances.
  'document-header.title.edit-hint': 'Click to edit title',
  'document-header.title.save-hint': 'Save title',
  'document-header.description.edit-hint': 'Click to edit description',
  'document-header.description.add-hint': 'Click to add a description',
  'document-header.description.placeholder': 'Add a description',
  'document-header.description.save-hint': 'Save description',
  'document-header.description.save-button': 'Save Description',
  'document-header.short-description.label': 'Title (max 200 characters):',
  'document-header.short-description.character-count': '{$count}/200',
  'document-header.long-description.label': 'Description (max 10000 characters):',
  'document-header.long-description.character-count': '{$count}/10000',
  // The change batch panel at the head of the docked navigator (ChangeBatchPanel.tsx)
  // and its edit dialog (ChangeBatchDetailsDialog.tsx).
  'change-batch-panel.aria': 'This change batch',
  'change-batch-panel.edit': 'Edit title and description',
  'change-batch-panel.dialog.heading': 'Edit draft details',
  'change-batch-panel.dialog.title-label': 'Title',
  'change-batch-panel.dialog.description-label': 'Description',
  // Document settings page (DocumentSettings.tsx).
  'document-settings.page-title-suffix': 'Settings',
  'document-settings.load-error': 'Failed to load document settings',
  'document-settings.not-found': 'Document Not Found',
  'document-settings.details.heading': 'Document Name / Description',
  'document-settings.details.saved': 'Document name and description saved',
  'document-settings.details.error': 'Failed to update document name and description',
  'document-settings.name.label': 'Name',
  'document-settings.description.label': 'Description',
  'document-settings.description.placeholder': 'Add a document description...',
  'document-settings.description.character-count': '{$count}/1000',
  'document-settings.rule.saved': 'Approval rule saved successfully',
  'document-settings.teams.heading': 'Teams',
  'document-settings.teams.help': 'Document teams can view the document and propose changes. Whether those changes are approved automatically or require approval depends on the approval rule and the teams selected for it.',
  'document-settings.teams.column.team': 'Team',
  'document-settings.teams.column.actions-aria': 'Actions',
  'document-settings.teams.in-rule': 'Part of approval rule',
  'document-settings.teams.remove-blocked': 'Remove this team from the approval rule first',
  'document-settings.remove-team.confirm.title': 'Remove team from document',
  'document-settings.remove-team.confirm.message': '"{$team}" will no longer have access to this document.',
  'document-settings.remove-team.confirm.action': 'Remove Team',
  'document-settings.remove-team.in-use-error': 'Cannot remove "{$team}" — it is part of the approval rule. Update the approval rule first.',
  'document-settings.remove-team.error': 'Failed to remove team from document',
  'document-settings.delete.heading': 'Delete Document',
  'document-settings.delete.button': 'Delete Document',
  'document-settings.delete.confirm.title': 'Delete this document',
  'document-settings.delete.confirm.message': 'This will move "{$document}" to the workspace\'s Recently deleted list. Workspace owners can restore it from there or delete it permanently.',
  'document-settings.delete.confirm.action': 'Delete Document',
  'document-settings.delete.error': 'Failed to delete document',
  // Document approval rules page (DocumentApprovalRules.tsx) — picks the
  // workspace rule that governs the document, reached from the document subnav
  // and gated on `canSetApprovalRule`.
  'document-approval-rules.page-title-suffix': 'Approval Rules',
  'document-approval-rules.load-error': 'Failed to load document',
  'document-approval-rules.not-found': 'Document Not Found',
  'document-approval-rules.no-access': 'You do not have access to manage this document\'s approval rules.',
  // Document labels page (DocumentLabels.tsx) — the label-vocabulary
  // management surface, reached from the document subnav and gated on
  // `canManageLabels`.
  'document-labels.page-title-suffix': 'Labels',
  'document-labels.load-error': 'Failed to load labels',
  'document-labels.not-found': 'Document Not Found',
  'document-labels.no-access': 'You do not have access to manage this document\'s labels.',
  'document-labels.help': "Labels are the document's shared vocabulary for tagging and filtering details. Archived labels stay visible wherever they were already applied but can no longer be attached to details.",
  'document-labels.add-placeholder': 'New label name...',
  'document-labels.add-button': 'Add Label',
  'document-labels.add-error': 'Failed to add label',
  'document-labels.archive-error': 'Failed to update label',
  'document-labels.empty': "No labels yet. Add one above, or create labels inline while editing a detail's labels.",
  'document-labels.column.label': 'Label',
  'document-labels.column.actions-aria': 'Actions',
  'document-labels.archived-badge': 'Archived',
  'document-labels.archive': 'Archive',
  'document-labels.unarchive': 'Restore',
  // Reconciliation flow (DetailReconciliation.tsx).
  'reconciliation.heading': "Update this draft's parent version",
  'reconciliation.no-update-needed': 'No update needed.',
  'reconciliation.loading': 'Loading latest approved version...',
  'reconciliation.no-operations': 'No operations to update.',
  'reconciliation.info': 'This draft cannot be approved because its parent is not the latest approved version. The following shows which operations can be kept or need to be removed.',
  'reconciliation.decision.keep': 'Keep',
  'reconciliation.decision.remove': 'Remove',
  'reconciliation.all-removed.help': 'All changes in this draft are no longer valid against the latest approved version. The best course of action is to delete this draft and start fresh.',
  'reconciliation.update.help': 'This will update the draft to be based on the latest approved version and drop operations that are no longer valid.',
  'reconciliation.update.help-no-ops': 'This draft has no operations to review. Updating will rebase it on the latest approved version.',
  'reconciliation.update.button': 'Update parent version',
  'reconciliation.update.busy': 'Updating...',
  'reconciliation.delete.button': 'Delete Draft',
  'reconciliation.delete.busy': 'Deleting...',
  'reconciliation.delete.confirm.title': 'Delete this draft',
  'reconciliation.delete.confirm.message': 'This draft and all its operations will be permanently deleted. This action cannot be undone.',
  'reconciliation.delete.confirm.action': 'Delete Draft',
  'reconciliation.errors.load-latest-failed': 'Failed to load latest approved version',
  'reconciliation.errors.update-parent-failed': "Failed to update draft's parent version",
  'reconciliation.errors.delete-failed': 'Failed to delete draft change batch',
  // Deferred-merge prompt: a copied detail has met its origin on the new
  // baseline, so the user decides whether they are one detail or two.
  'reconciliation.copies.heading': 'Copied details that met their origin',
  'reconciliation.copies.info': 'These details were copied from ones now in the latest approved version. Keep them separate, or merge each onto the approved detail it was copied from.',
  'reconciliation.copies.choice-label': 'Merge or keep both',
  'reconciliation.copies.keep-both': 'Keep both',
  'reconciliation.copies.merge': 'Merge',
  'reconciliation.copies.diverged': 'Content differs — merging keeps your wording as an edit of the approved detail.',
  // Op redistribution: selecting details in the tree to split their operations
  // out into a new draft (RedistributeActionBar / SelectionCheckbox).
  'redistribute.start': 'Move or copy details',
  // Page title suffix while details are being chosen to move or copy; the
  // dock and the batch's heading step aside for the task.
  'redistribute.heading': 'Move or copy details',
  'redistribute.heading-help': 'Tick the items to move or copy, then choose where they go in the bar below.',
  'redistribute.start-empty-hint': 'This draft has no changes yet — nothing to move or copy.',
  'redistribute.bar-label': 'Redistribute selected operations',
  'redistribute.select-detail': 'Select "{$content}" to move',
  'redistribute.comes-along': 'Comes along — a selected detail depends on it',
  'redistribute.selected-count': 'Selected: {$count}',
  'redistribute.comes-along-count': '+{$count} pulled in by dependencies',
  'redistribute.op-count': '{$count} operations',
  'redistribute.split-to-new': 'Split to new draft',
  'redistribute.move-to': 'Move to draft…',
  'redistribute.copy-to-new': 'Copy to new draft',
  'redistribute.copy-to': 'Copy to draft…',
  'redistribute.no-siblings': 'No same-parent draft to send into',
  'redistribute.dest-label': 'Destination draft',
  'redistribute.move.confirm': 'Move',
  'redistribute.copy.confirm': 'Copy',
  'redistribute.copy-new.confirm': 'Create copy',
  'redistribute.busy': 'Working…',
  'redistribute.cancel': 'Cancel',
  'redistribute.back': 'Back',
  'redistribute.split.short-placeholder': 'New draft title',
  'redistribute.split.long-placeholder': 'What this draft covers',
  'redistribute.split.confirm': 'Create draft',
  // Merge: fold this whole draft into a same-parent sibling and delete it.
  // The entry point is a draft action (DraftActions.tsx); the picker and
  // confirmation live on their own page (MergeChangeBatch.tsx).
  'merge.start': 'Merge into another draft',
  'merge.warning': "Merging moves this draft's changes into the chosen draft and deletes this one. This can't be undone.",
  'merge.dest-label': 'Merge into',
  'merge.confirm': 'Merge and delete this draft',
  'merge.busy': 'Merging…',
  // Page title suffix of the merge page, after the document's name.
  'merge.page-title-suffix': 'Merge draft',
  'merge.no-siblings': 'There is no other draft under the same version to combine this one with.',
  'merge.back': 'Back to draft',
  // The draft's checkpoint history page (DraftCheckpoints.tsx), reached
  // from the draft's action row.
  'checkpoints.open': 'Checkpoint history',
  'checkpoints.page-title-suffix': 'Checkpoint history',
  'checkpoints.back': 'Back to draft',
  'checkpoints.empty': 'Only a draft has checkpoints. This change batch has none to show.',
  // Approval rules: named rules (AND-groups of teams / referenced
  // rules, any-of within a group). Shared between the workspace registry
  // page (WorkspaceApprovalRules.tsx) where rules are created and
  // edited, and the document-settings picker (DocumentApprovalRulePicker.tsx)
  // that binds one to a document.
  'approval-rules.heading': 'Approval Rules',
  'approval-rules.help': 'Named policies describing who must sign off. Every row must be satisfied; a row with alternatives is satisfied by any one of them.',
  'approval-rules.loading': 'Loading approval rules...',
  'approval-rules.teams-error': 'Failed to load teams. Please refresh and try again.',
  'approval-rules.document-rule.label': 'Document approval rule',
  'approval-rules.document-rule.none': 'None — no approval required',
  'approval-rules.document-rule.help': 'The document approval rule applies to every change batch in this document.',
  'approval-rules.document-rule.save': 'Set',
  'approval-rules.archived-badge': 'Archived',
  'approval-rules.create.button': 'New Rule',
  'approval-rules.create.save': 'Create Rule',
  'approval-rules.update.save': 'Update Rule',
  'approval-rules.edit': 'Edit',
  'approval-rules.archive': 'Archive',
  'approval-rules.unarchive': 'Unarchive',
  'approval-rules.delete': 'Delete',
  'approval-rules.delete.confirm.title': 'Delete approval rule',
  'approval-rules.delete.confirm.message': 'Permanently delete “{$name}”? It has never been used, so this cannot be undone.',
  'approval-rules.delete.confirm.action': 'Delete',
  'approval-rules.name.label': 'Name',
  'approval-rules.groups.help': 'Each condition below must be satisfied. Add alternatives within a condition to accept any one of them.',
  'approval-rules.add-group': 'Add condition',
  'approval-rules.remove-group': 'Remove condition',
  'approval-rules.add-atom': 'Add team or rule…',
  'approval-rules.remove-atom': 'Remove {$name}',
  'approval-rules.rule-option': 'Rule: {$name}',
  'approval-rules.and': 'and',
  'approval-rules.or': 'or',
  'approval-rules.unknown-team': 'Unknown team',
  'approval-rules.unknown-rule': 'Unknown rule',
  'approval-rules.validation.name-required': 'Enter a rule name',
  'approval-rules.validation.empty-group': 'Every condition needs at least one team or rule',
  'approval-rules.save.error': 'Failed to save approval rule',
  'approval-rules.team-conflict.heading': 'This change can’t be saved yet',
  'approval-rules.team-conflict.help': 'Every team a rule requires must be assigned to each document using it, which lets that team see the document. Assign the missing teams below, or leave them out of the rule.',
  'approval-rules.team-conflict.assign': 'Assign {$team}',
  'approval-rules.team-conflict.assigned': '{$team} assigned',
  'approval-rules.team-conflict.assign-error': 'Could not assign {$team} to {$document}.',
  'approval-rules.manage-link': 'Manage approval rules →',
  // Workspace approval rules page (WorkspaceApprovalRules.tsx) — the
  // workspace-scoped registry where rules are created, edited, and archived.
  'workspace-approval-rules.page-title': 'Approval Rules',
  // One rule's page (WorkspaceApprovalRule.tsx): its builder, archive or
  // delete action, and the documents it is in force on.
  'approval-rule.back': '← All approval rules',
  'approval-rule.not-found': 'This approval rule doesn’t exist in this workspace.',
  'approval-rule.saved': 'Rule saved.',
  'approval-rule.definition.heading': 'Definition',
  'approval-rule.documents.heading': 'Documents',
  'approval-rule.documents.help': 'Documents where this rule decides who must sign off.',
  'approval-rule.documents.loading': 'Loading documents...',
  'approval-rule.documents.empty': 'Not applied to any document yet. Apply it from a document’s settings.',
  'approval-rule.documents.applied-as.baseline': 'Document rule',
  'approval-rule.documents.applied-as.detail': 'On details',
  'approval-rule.documents.applied-as.nested': 'Through another rule',
  'workspace-approval-rules.binding-hint': 'Apply approval to documents or details.',
  'workspace-approval-rules.teams-empty': 'No teams available. Create a team first, then build a rule from your teams.',
  // Workspace people page (WorkspacePeople.tsx).
  'workspace-people.page-title': 'People',
  'workspace-people.description': 'Everyone with access to this workspace. Administrators have full access; members are added through teams.',
  'workspace-people.loading': 'Loading people...',
  'workspace-people.not-found': 'Workspace not found',
  'workspace-people.no-permission': "You don't have permission to manage members for this workspace.",
  'workspace-people.column.member': 'Member',
  'workspace-people.column.role': 'Role',
  'workspace-people.column.joined': 'Joined',
  'workspace-people.column.actions-aria': 'Actions',
  'workspace-people.remove.confirm.title': 'Remove workspace member',
  'workspace-people.remove.confirm.message': 'This member will lose access to the workspace.',
  'workspace-people.remove.confirm.action': 'Remove Member',
  'workspace-people.remove.error': 'Failed to remove member',
  // Workspace invitation codes page (WorkspaceInvite.tsx).
  'workspace-invite.heading': 'Invite',
  'workspace-invite.description': "Generate invitation codes to invite new administrators to this workspace. Administrators have full access to manage the workspace, its teams, and documents. If you are looking to add collaborators to a specific team, visit that team's settings instead.",
  'workspace-invite.column.code': 'Code',
  'workspace-invite.column.scope': 'Scope',
  'workspace-invite.column.created': 'Created',
  'workspace-invite.column.expires': 'Expires',
  'workspace-invite.column.status': 'Status',
  'workspace-invite.column.actions-aria': 'Actions',
  'workspace-invite.scope.team': 'Team: {$team}',
  'workspace-invite.scope.workspace': 'Workspace (owner)',
  'workspace-invite.status.used': 'Used',
  'workspace-invite.status.active': 'Active',
  'workspace-invite.status.expired': 'Expired',
  'workspace-invite.copy.button': 'Copy Link',
  'workspace-invite.copy.done': 'Copied!',
  'workspace-invite.copy.fallback-prompt': 'Copy invitation link:',
  'workspace-invite.revoke.button': 'Revoke',
  'workspace-invite.revoke.busy': 'Revoking...',
  'workspace-invite.revoke.error': 'Failed to revoke invitation code',
  'workspace-invite.generate.button': 'Generate Invitation Code',
  'workspace-invite.generate.busy': 'Generating Code...',
  'workspace-invite.generate.error': 'Failed to generate invitation code',
  // Profile page (Profile.tsx).
  'profile.heading': 'Profile',
  'profile.description': 'Your personal information',
  'profile.email.label': 'Email',
  'profile.display-name.label': 'Display Name',
  'profile.display-name.update-error': 'Failed to update display name',
  'profile.theme.label': 'Theme',
  // ProfileJoin page (ProfileJoin.tsx).
  // Connected apps page (ConnectedApps.tsx).
  'connected-apps.heading': 'Connected apps',
  'connected-apps.description': 'Apps and sessions that can act on your behalf. Revoke anything you do not recognize.',
  'connected-apps.empty': 'Nothing is connected to your account.',
  'connected-apps.column.name': 'App',
  'connected-apps.column.workspace': 'Workspace',
  'connected-apps.column.granted': 'Granted',
  'connected-apps.column.last-used': 'Last used',
  'connected-apps.column.actions-aria': 'Actions',
  'connected-apps.first-party-name': 'Goalpost web app',
  'connected-apps.first-party-hint': 'A signed-in browser session',
  'connected-apps.workspace.any': 'All your workspaces',
  'connected-apps.never': 'Never',
  'connected-apps.revoke.button': 'Revoke',
  'connected-apps.revoke.busy': 'Revoking…',
  'connected-apps.revoke.error': 'Failed to revoke access',
  'connected-apps.revoke.confirm-title': 'Revoke access for {$name}?',
  'connected-apps.revoke.confirm-message.third-party': '{$name} will lose access immediately and will have to be connected again to get it back.',
  'connected-apps.revoke.confirm-message.first-party': 'That browser session will be signed out. If it is the one you are using now, you will be signed out here too.',
  // Passkeys page (Passkeys.tsx).
  'passkeys.heading': 'Passkeys',
  'passkeys.description': 'Sign in with your fingerprint, face, or screen lock instead of an email link. Add a passkey on each device you use, or one your password manager syncs between them.',
  'passkeys.unsupported': 'This browser does not support passkeys.',
  'passkeys.empty': 'You have no passkeys yet.',
  'passkeys.column.name': 'Passkey',
  'passkeys.column.created': 'Added',
  'passkeys.column.last-used': 'Last used',
  'passkeys.column.actions-aria': 'Actions',
  'passkeys.kind.synced': 'Synced across your devices',
  'passkeys.kind.device': 'On one device or security key',
  'passkeys.never': 'Never',
  'passkeys.add.name-label': 'Name',
  'passkeys.add.name-placeholder': 'e.g. Work laptop',
  'passkeys.add.button': 'Add a passkey',
  'passkeys.add.busy': 'Waiting for your passkey...',
  'passkeys.add.error': 'Failed to add the passkey',
  'passkeys.add.already-registered': 'This device already has a passkey for your account.',
  'passkeys.remove.button': 'Remove',
  'passkeys.remove.busy': 'Removing…',
  'passkeys.remove.error': 'Failed to remove the passkey',
  'passkeys.remove.confirm-title': 'Remove {$name}?',
  'passkeys.remove.confirm-message': 'It will no longer sign in to your account. Sessions it already signed in stay signed in; revoke those under Connected apps. You may also want to delete it from your device or password manager.',
  'profile-join.heading': 'Join a Workspace',
  'profile-join.description': 'Enter an invitation code to join an existing workspace. Ask a workspace owner to generate a code for you. Codes are in format XXXX-XXXX-XXXX and expire after 7 days.',
  'profile-join.code.label': 'Invitation Code',
  'profile-join.code.placeholder': 'XXXX-XXXX-XXXX',
  'profile-join.submit': 'Join Workspace',
  'profile-join.submitting': 'Joining Workspace...',
  'profile-join.success': 'Successfully joined workspace',
  'profile-join.redirecting': 'Redirecting to documents...',
  'profile-join.validation.code-incomplete': 'Please enter a complete invitation code',
  'profile-join.errors.join-failed': 'Failed to join workspace',
  // JoinWithInvitation page (JoinWithInvitation.tsx).
  'join.success': 'Successfully joined! Redirecting...',
  'join.authenticated.heading': 'Join a Workspace',
  'join.authenticated.description': 'You have been invited to join a workspace. Click below to accept the invitation.',
  'join.accept.button': 'Accept Invitation',
  'join.accept.busy': 'Joining...',
  'join.anonymous.heading': "You've Been Invited",
  'join.anonymous.description': 'You need to sign in or create an account to accept this invitation.',
  'join.anonymous.login': 'I have an account — Login',
  'join.anonymous.register': "I'm new — Register",
  'join.errors.join-failed': 'Failed to join workspace',
  // Creating the invited account straight from the invitation email's link.
  'join.anonymous.register-now': "I'm new — Create my account",
  'join.register.busy': 'Creating your account...',
  'join.errors.account-exists': 'An account with this email already exists. Sign in to accept the invitation.',
  'join.errors.register-failed': 'Could not create your account. Please try again.',
  'join.scope.workspace': 'You have been invited to join {$workspace} as an owner.',
  'join.scope.team': 'You have been invited to join the {$team} team in {$workspace}.',
  'join.sent-to': 'This invitation was sent to {$email}. Sign in or register with that address to accept it.',
  'join.logout': 'Log Out to Switch Accounts',
  'join.wrong-account': "You're signed in as {$current}, but this invitation was sent to {$email}. Log out and sign in with that address to accept it.",
  // Invite-by-email form (InviteByEmailForm.tsx), shared by the workspace and
  // team invite pages.
  'invite-email.label': 'Invite by email',
  'invite-email.placeholder': 'name@example.com, another@example.com',
  'invite-email.hint': 'Each person gets their own invitation link, which only works for the email address it was sent to.',
  'invite-email.submit': 'Send Invitations',
  'invite-email.busy': 'Sending...',
  'invite-email.error': 'Failed to send invitations',
  'invite-email.validation.empty': 'Enter at least one email address',
  'invite-email.validation.invalid': 'Not a valid email address: {$emails}',
  'invite-email.validation.too-many': 'You can invite up to {$max} people at a time',
  'invite-email.sent': 'Invitations sent: {$count}',
  'invite-email.undelivered': "These invitations were created but the email couldn't be sent. Use Resend to try again: {$emails}",
  // Per-row affordances for emailed invitations on both invite pages.
  'invite-email.sent-to': 'Sent to {$email}',
  'invite-email.not-sent': 'Not sent to {$email}',
  'invite-email.resend.button': 'Resend',
  'invite-email.resend.busy': 'Sending...',
  'invite-email.resend.done': 'Sent!',
  'invite-email.resend.error': 'Failed to resend invitation',
  // OAuth consent page (OAuthConsent.tsx).
  'oauth-consent.heading': 'Authorize Application',
  'oauth-consent.request-description': 'is requesting access to your Goalpost workspace.',
  'oauth-consent.scope': 'Scope: {$scope}',
  'oauth-consent.scope.mcp': "This lets it read your documents and create or update draft change batches on your behalf. If you own the workspace, it can also manage teams, their members and document access, and approval rules. It cannot submit approvals or change workspace settings.",
  'oauth-consent.scope.api': 'This lets it do everything you can do in Goalpost.',
  'oauth-consent.destination.web': 'If you allow, access is handed to {$host}.',
  'oauth-consent.destination.local': 'If you allow, access is handed to an app running on this device.',
  'oauth-consent.unverified': "Goalpost can't verify who published {$clientName}. Only continue if you started this connection yourself, from an app you trust.",
  'oauth-consent.no-workspaces': 'No workspaces available. You need at least one workspace to authorize this application.',
  'oauth-consent.no-mcp-workspaces': 'None of your workspaces can connect an AI assistant.',
  'oauth-consent.grant.single': 'Grant access to this workspace:',
  'oauth-consent.grant.multiple': 'Select a workspace to grant access:',
  'oauth-consent.allow.button': 'Allow',
  'oauth-consent.allow.busy': 'Authorizing…',
  'oauth-consent.deny.button': 'Deny',
  'oauth-consent.error.heading': 'Authorization Error',
  'oauth-consent.error.missing-params': 'Missing or invalid consent parameters.',
  'oauth-consent.complete.heading': 'Authorization Complete',
  'oauth-consent.complete.description': 'You have authorized {$clientName}. You can close this window and return to the application.',
  'oauth-consent.denied.heading': 'Access Denied',
  'oauth-consent.denied.description': 'You denied the authorization request. You can close this window.',
  'oauth-consent.errors.submission-failed': 'Consent submission failed',
  // Skip link — the first tab stop on every page in both frontends, letting a
  // keyboard user bypass the header and sidebar and land in <main>.
  'skip-link.main-content': 'Skip to main content',
  // Hamburger / theme toggle.
  'hamburger-menu.toggle-aria': 'Toggle navigation menu',
  'theme-toggle.switch-to-light': 'Switch to light theme',
  'theme-toggle.switch-to-dark': 'Switch to dark theme',
  'theme-toggle.label.dark': 'Dark',
  'theme-toggle.label.light': 'Light',
  // Workspace settings page (Workspace.tsx).
  'workspace.page-title-suffix': 'Settings',
  'workspace.description': 'Workspace information and preferences',
  'workspace.name.heading': 'Workspace Name',
  'workspace.name.label': 'Name',
  'workspace.name.saved': 'Workspace name saved',
  'workspace.name.error': 'Failed to update workspace name',
  'workspace.column.workspace': 'Workspace',
  'workspace.column.role': 'Role',
  'workspace.trash.heading': 'Recently deleted',
  'workspace.trash.help': 'Deleted documents stay here until an owner restores them or deletes them permanently.',
  'workspace.trash.empty': 'No recently deleted documents.',
  'workspace.trash.column.document': 'Document',
  'workspace.trash.column.deleted': 'Deleted',
  'workspace.trash.column.actions-aria': 'Actions',
  'workspace.trash.restore': 'Restore',
  'workspace.trash.delete-permanently': 'Delete permanently',
  'workspace.trash.restored': '"{$document}" restored',
  'workspace.trash.deleted-permanently': '"{$document}" permanently deleted',
  'workspace.trash.restore.duplicate-name': 'A document named "{$document}" already exists. Rename it, then restore this one.',
  'workspace.trash.restore.limit': 'This workspace is at its document limit. Remove a document or upgrade your plan to restore this one.',
  'workspace.trash.restore.error': 'Failed to restore document',
  'workspace.trash.delete-permanently.confirm.title': 'Delete this document permanently',
  'workspace.trash.delete-permanently.confirm.message': 'This will permanently remove "{$document}" and all its change batches and details. This action cannot be undone.',
  'workspace.trash.delete-permanently.confirm.action': 'Delete permanently',
  'workspace.trash.delete-permanently.error': 'Failed to delete document',
  'workspace.trash.load-error': 'Failed to load recently deleted documents',
  // Document change batches page (DocumentChangeBatches.tsx).
  'document-change-batches.filter-aria': 'Filter change batches',
  'document-change-batches.filter.approved.label': 'History',
  'document-change-batches.filter.approved.description': 'Approved versions in this document',
  'document-change-batches.filter.in-progress.label': 'In Progress',
  'document-change-batches.filter.in-progress.description': 'Drafts and pending-approval change batches in this document',
  'document-change-batches.filter.all.label': 'All Changes',
  'document-change-batches.filter.all.description': 'Browse all changes in this document',
  'document-change-batches.empty.approved.heading': 'No approved versions yet',
  'document-change-batches.empty.approved.description': 'Once a change batch is approved it will appear here.',
  'document-change-batches.empty.in-progress.description': 'Batches of changes you\'re working on will appear here.',
  'document-change-batches.empty.all.heading': 'No changes yet',
  'document-change-batches.empty.all.description': 'Create a draft to get started.',
  'document-change-batches.load-error': 'Failed to load change batches',
  'document-change-batches.column.change-batch': 'Batch',
  'document-change-batches.column.status': 'Status',
  'document-change-batches.column.date': 'Date',
  // Date-prefix variants on the change batch row. Concatenated with the
  // formatted date at the call site, mirroring the prefix convention
  // used elsewhere (team-invites.used-prefix, detail-tree.*).
  'document-change-batches.row.approved-date': 'Approved {$date}',
  'document-change-batches.row.created-date': 'Created {$date}',
  // OAuthAuthorize page (OAuthAuthorize.tsx) — `logging-in` is distinct
  // from `login.password.submitting` because it appears alongside the
  // OAuth password form (which submits to the authorize endpoint, not
  // the webapp login endpoint) and may diverge later.
  'oauth-authorize.logging-in': 'Logging in...',
  // Change batch details page (ChangeBatchDetails.tsx) — top-level loading,
  // not-found, and the tab labels for the four detail views. Many of
  // these keys are also reused by the document Overview page
  // (DocumentOverview.tsx), which renders the specification read-only.

  'document-details.loading': 'Loading document...',
  'document-details.not-found.heading': 'Document Not Found',
  'document-details.not-found.description': "The document you're looking for doesn't exist.",
  // Document Overview version badges (DocumentOverview.tsx). Rendered as a
  // pair in the toolbar above the specification to identify the
  // approved version currently being shown.
  'document-overview.version-badge.label': 'v{$version}',
  'document-overview.version-badge.tooltip': 'Version {$version}',
  'document-overview.approved-badge': 'Approved {$date}',
  // Primary CTA in the overview tabbar (DocumentOverview.tsx) — links to the
  // Propose Changes form (/new-change-batch) so users can start an edit.
  'document-overview.propose-changes': 'Propose changes',
  // Notice above the specification while the draft needs its parent
  // updated.
  'document-details.rebase-preview': 'Preview of the tree after updating the parent version. Update to edit.',
  'document-details.details-loading': 'Loading details...',
  // The "Changes only" view's empty state.
  'document-details.changes.empty': 'No changes in this change batch yet. Edits you make to the document will appear here.',
  // Canonical one-line summary of what a change batch is. Shared so the
  // product and the marketing docs stay in sync: rendered as the opening of
  // the /docs/change-management "Change batches" section and as the lead of
  // the document overview empty state.
  'feature.change-batch.summary': "Every modification to a document is captured as one of the {#strong}changes{/strong} in a {#strong}change batch{/strong}. Changes are grouped into a batch so they're drafted, reviewed, and approved together.",
  // "Learn more" link to /docs/change-management, shown after the summary on
  // both the create-draft page and the empty-document view.
  'feature.change-batch-learn-more': 'Learn more about change batches',
  // Canonical summary of what approval rules are and how they layer: defined in
  // a workspace, applied to documents or individual details, deciding which teams
  // must sign off and how each team counts its own sign-off. Opens the
  // /docs/approval-rules page. {#strong} bolds key terms; the concept markup
  // ({#workspace}, {#documents}, {#details}, {#teams}, {#changes}) is mapped to
  // doc links by that page and degrades to plain text anywhere that doesn't.
  'feature.approval-rules.summary': '{#strong}Approval rules{/strong} let you define who approves your changes. You can define approval rules in your {#workspace}workspace{/workspace}, which can then be applied to your {#documents}documents{/documents}, or even to individual {#details}details{/details} within your documents. These decide which {#teams}teams{/teams} need to sign off on a particular {#changes}batch of changes{/changes}. Additionally, you can define rules for how each team signs off — one team might request {#strong}any two people{/strong} to approve, another may require {#strong}every team member{/strong} to approve before that team has signed off.',
  // Empty-document view (DocumentOverview.tsx): shown when the document has no
  // change batches yet. Rather than collecting a detail up front, it inlines
  // the CreateDraftForm so users take the natural first step — creating a
  // draft change batch. The lead reuses feature.change-batch.summary (with an
  // adjacent Learn more link to the docs); this prompt follows it, and the
  // placeholders suggest a name and starting scope for that first draft.
  'document-overview.empty-state.prompt': 'Create your first draft change batch to start adding details.',
  'document-overview.empty-state.draft-name-placeholder': 'First draft',
  'document-overview.empty-state.draft-description-placeholder': 'Define initial components and document structure',
  // DetailChangesList.tsx Changes-tab section. Concatenated at the call
  // site with a button rendering the parent change batch's label.
  'detail-changes.changes-since-prefix': 'Changes since',
  // "Changes only" scoped to a section the version changed nothing in.
  'detail-changes.empty-in-section': 'No changes in this section.',
  // Accessible label for the breadcrumb of unchanged ancestors shown above a
  // group of changes on the Changes tab, naming where in the tree they land.
  'detail-changes.location-aria': 'Change location',
  // Detail-operation descriptions rendered by getDetailOperationDescription
  // (webapp/src/utils/detail-operations.ts) on the Changes tab, the
  // reconciliation list, and the create-draft pending-change banner. Kept
  // separate from the email-side detail-tree.* keys: that renderer is plain
  // text, whereas these back JSX where content is wrapped in <strong>. The
  // call site truncates content to 80 chars.
  //
  // ADD / REMOVE / REPLACE / MOVE each have their own icon on the row, so their
  // phrasing here backs the row's accessible name and hover text while the row
  // shows the content. ADD / REPLACE always carry content (the operation schema
  // requires it), so they only have a `.with-content` template; REMOVE / MOVE
  // read content from a lookup that can miss, so they keep a bare form for a
  // row with nothing to show.
  'detail-operation.added.with-content': 'Added detail: {$content}',
  'detail-operation.removed': 'Removed a detail',
  'detail-operation.removed.with-content': 'Removed: "{$content}"',
  'detail-operation.replaced.with-content': 'Replaced detail: {$content}',
  'detail-operation.moved': 'Moved a detail',
  'detail-operation.moved.with-content': 'Moved: "{$content}"',
  // The label and approval-rule ops share one icon per family, which can't say
  // which direction the change went, so these render their verb on the row
  // itself, with the affected content bolded after a `.prefix`.
  'detail-operation.label-added': 'Added a label',
  'detail-operation.label-added.with-label': 'Added label "{$label}"',
  'detail-operation.label-added.prefix': 'Added label "{$label}" to:',
  'detail-operation.label-removed': 'Removed a label',
  'detail-operation.label-removed.with-label': 'Removed label "{$label}"',
  'detail-operation.label-removed.prefix': 'Removed label "{$label}" from:',
  // SET_DETAIL_APPROVAL_RULE. The rule name resolves from the workspace registry
  // when threaded; the `-generic` forms cover previews where it isn't.
  'detail-operation.rule-set.with-rule': 'Set approval rule "{$rule}"',
  'detail-operation.rule-set.prefix': 'Set approval rule "{$rule}" on:',
  'detail-operation.rule-set-generic': 'Set an approval rule',
  'detail-operation.rule-set-generic.prefix': 'Set an approval rule on:',
  'detail-operation.rule-cleared': 'Cleared the approval rule',
  'detail-operation.rule-cleared.prefix': 'Cleared the approval rule on:',
  'detail-operation.unknown': 'Unknown operation: {$operationType}',
  // DetailTree.tsx empty state: a heading plus, in a draft, a hint about
  // the form underneath it.
  'detail-tree.empty.heading': 'No details yet.',
  'detail-tree.empty.draft-hint': 'Use the form below to add your first section, then add details inside it.',
  'detail-tree.empty.scoped-heading': 'This section has no details yet.',
  'detail-tree.empty.scoped-hint': 'Use the form below to add the first detail to this section.',
  // DetailTree.tsx collapse-all / expand-all toolbar, shown above the document
  // whenever at least one detail has children.
  'detail-tree.expand-all': 'Expand all',
  'detail-tree.collapse-all': 'Collapse all',
  // RemovedDetailRow.tsx ghost row for details that lived in the parent
  // change batch but were removed in the current one. Mirrors
  // `detail.content.empty` so the empty-content fallback reads
  // consistently across views.
  'detail.removed.empty': '(empty)',
  // ApprovalStatus.tsx — confirm dialogs for withdraw / reject actions.
  // Each pair (title + message + action) is read together by the
  // useConfirm hook.
  'approval-status.withdraw.confirm.title': 'Withdraw approval request',
  'approval-status.withdraw.confirm.message': 'This change batch will return to draft state.',
  'approval-status.withdraw.confirm.action': 'Withdraw',
  'approval-status.reject.confirm.title': 'Reject this change batch',
  'approval-status.reject.confirm.message': 'It will return to draft state and all approvals will be cleared.',
  'approval-status.reject.confirm.action': 'Reject',
  // Auto-approve banner shown when the final required approval flips
  // the change batch to `approved`.
  'approval-status.auto-approved': 'All approvals received — change batch has been automatically approved!',
  // Collapsible Status section header label (the badge text comes
  // from `approval-status.state.*` below).
  'approval-status.heading': 'Status',
  // State pills rendered next to the Status header. `not-ready` lights
  // up on drafts that need rebasing; the rest follow the change batch
  // lifecycle (draft → pending → approved/active).
  'approval-status.state.not-ready': 'Not Ready',
  'approval-status.state.draft': 'Draft',
  'approval-status.state.satisfied': 'Satisfied',
  'approval-status.state.pending': 'Pending',
  'approval-status.state.active': 'Active',
  'approval-status.state.approved': 'Approved',
  // Gray version pill shown beside the state pill once a change batch is
  // approved and carries a version number (e.g. "v3").
  'approval-status.version-badge.label': 'v{$version}',
  'approval-status.version-badge.tooltip': 'Version {$version}',
  // Matched-rule breakdown: the document's approval rule resolved
  // against this change batch. `rules.heading` names the rule;
  // `group.any-of` labels OR-groups; `quorum.progress` shows per-team
  // approval counts against the quorum target.
  'approval-status.rules.heading': 'Required approvals — {$name}',
  'approval-status.provenance.baseline': 'Required for every change',
  'approval-status.provenance.detail': 'Required by an edited detail',
  'approval-status.provenance.detail-named': 'Required by: {$names}',
  'approval-status.group.any-of': 'Any of:',
  'approval-status.quorum.progress': '{$received} of {$required} approved',
  'approval-status.team-approval.approved': 'Approved',
  'approval-status.team-approval.pending': 'Pending',
  // Copy for documents with no approval rule (no approval required).
  // The `.approved` variant reads past-tense once the batch is approved.
  'approval-status.none.description': 'No approval required — submitting will approve immediately',
  'approval-status.none.description.approved': 'When no approval rule is set, changes are automatically approved once submitted.',
  // Pre-submit copy shown on drafts above the submit CTA. `none` covers
  // the no-approval-required path; `with-rule` names the document
  // approval rule whose breakdown is shown above.
  'approval-status.pre-submit.none': "Submitting this draft will approve it and add it to the document's history immediately. Once approved, the change is part of the permanent record.",
  'approval-status.pre-submit.with-rule': "Submitting moves your draft to pending approval and notifies the required teams under “{$name}”. You can withdraw it from this page until it's approved.",
  'approval-status.pre-submit.with-rules': 'Submitting moves your draft to pending approval and notifies the required teams for: {$names}. You can withdraw it from this page until it\'s approved.',
  // Submit / approve / reject / withdraw buttons. `submit.approve`
  // replaces `submit.submit` when the document's rule is `none`
  // (clicking the CTA directly approves the change batch).
  'approval-status.submit.approve': 'Approve Changes',
  'approval-status.submit.submit': 'Submit for Approval',
  // Per-team approve/reject actions, rendered inline on each team's row in the
  // breakdown above. `.button` is the short visible label (the team is named
  // by the adjacent row); `.for-team` is the descriptive accessible name
  // (aria-label) so the action is unambiguous for screen readers and tests.
  'approval-status.approve.button': 'Approve',
  'approval-status.approve.for-team': 'Approve for {$team}',
  'approval-status.reject.button': 'Reject',
  'approval-status.reject.for-team': 'Reject for {$team}',
  'approval-status.withdraw.button': 'Withdraw Approval Request',
  // ApprovalStatus.tsx — the append-only governance timeline: every submit,
  // approve, reject, and withdraw on this change batch, in order, so the full
  // journey (including rework loops) stays visible.
  'approval-status.history.heading': 'History',
  'approval-status.history.submitted': 'Submitted for approval',
  'approval-status.history.approved': 'Approved by {$team}',
  'approval-status.history.rejected': 'Rejected by {$team}',
  'approval-status.history.withdrawn': 'Withdrawn by author',
  // Shown for approve/reject entries whose team was later deleted (team_id is
  // ON DELETE SET NULL, so the history entry survives without the team).
  'approval-status.history.removed-team': 'a removed team',
  'approval-status.history.by-at': '{$actor} · {$when}',
  // CheckpointHistory.tsx — collapsible "Checkpoint history" section shown
  // while a change batch is in draft. Every save to the draft is kept as a
  // checkpoint; the list shows them newest first with a roll-back action on
  // each earlier checkpoint. After a rollback, the checkpoints rolled past
  // survive until the next save, so a single-step Redo is offered to reverse
  // the rollback.
  'checkpoint-history.heading': 'Checkpoint history',
  // CLDR plural — translators replace with locale-appropriate selector arms.
  'checkpoint-history.count': `.input {$count :integer}
.match $count
one {{{$count} checkpoint}}
*   {{{$count} checkpoints}}`,
  // Header badge signaling the draft sits on an earlier checkpoint (redo available).
  'checkpoint-history.rolled-back-badge': 'Rolled back',
  'checkpoint-history.help': 'Every save to this draft is kept here. Roll back to return the draft to an earlier checkpoint. Newer checkpoints stay available to redo until you make a new change — then they are discarded.',
  'checkpoint-history.redo.notice': 'This draft is rolled back to an earlier checkpoint.',
  'checkpoint-history.redo.button': 'Redo',
  'checkpoint-history.saved-by': 'Saved by {$name} · {$date}',
  'checkpoint-history.current-badge': 'Current',
  // `.button` is the visible label; `.aria` names the target checkpoint by its
  // sequence number for screen readers and tests, since several rows carry
  // the same button text and the visible rows are told apart by timestamp.
  'checkpoint-history.rollback.button': 'Roll back to this checkpoint',
  'checkpoint-history.rollback.aria': 'Roll back to checkpoint {$seq}',
  // Shown in place of the roll-back action on a checkpoint the server flags as
  // not restorable: one saved before the document's first approved version,
  // which can never be rolled back to once an approval exists. The checkpoint
  // stays listed so history doesn't silently vanish; `.badge` is the short
  // inline label and `.tooltip` explains why on hover.
  'checkpoint-history.unrestorable.badge': "Can't restore",
  'checkpoint-history.unrestorable.tooltip': "This checkpoint was saved before the document's first approved version and can no longer be restored.",
  // Shared marketing chrome used across Home, Pricing, and feature pages. CTAs and the cross-link/footer link
  // titles are deduped here so a copy change propagates to every
  // surface that references the same destination.
  'marketing.cta.signup-free': 'Sign up free',
  // Shorter signup label for the nav, where the full "Sign up free" crowds the
  // bar; the on-page CTA buttons keep `signup-free`.
  'marketing.cta.signup': 'Sign up',
  'marketing.cta.login': 'Log in',
  'marketing.cta.keep-reading': 'Keep reading',
  'marketing.crosslink.read-more': 'Read more →',
  'marketing.feature.documents.title': 'Working with Documents',
  'marketing.feature.approvals.title': 'Approval Rules',
  'marketing.feature.ai.title': 'AI Integration',
  // Short-form eyebrows used on CrossLinks panels (where a 2–3 char tag
  // visually anchors the destination). The feature pages themselves use
  // longer hero eyebrows defined per-page.
  'marketing.feature.documents.eyebrow': 'Documents',
  'marketing.feature.approvals.eyebrow': 'Approvals',
  'marketing.feature.ai.eyebrow': 'AI',
  'learn-more-menu.trigger': 'Learn More',
  'marketing-footer.tagline': 'Shared notes for people and agents.',
  'marketing-footer.col.features': 'Features',
  'marketing-footer.col.learn-more': 'Learn more',
  'marketing-footer.col.about-goalpost': 'About Goalpost',
  'marketing-footer.link.pricing': 'Pricing',
  'marketing-footer.link.signup': 'Sign up',
  'marketing-footer.copyright': '© {$year} On A Hill LLC.',
  'marketing-footer.link.privacy': 'Privacy',
  'marketing-footer.link.terms': 'Terms',
  'marketing-footer.link.blog': 'Blog',
  // Blog (pages/blog/*, components/blog/BlogArticle.tsx). Each post's name and
  // one-line summary live here under `blog.<slug>.*`; the body is inline prose.
  'blog.eyebrow': 'Blog',
  'blog.index.document-title': 'Blog',
  // The brand's name with the section's name picked out of it; the {#em}
  // half renders in the accent on the index page.
  'blog.index.title': 'Goal{#em}posts{/em}',
  'blog.index.lede': 'News and updates from and about Goalpost.',
  'blog.all-posts': '← All posts',
  'blog.goalpost-mcp-app.title': 'See Goalpost in your chat',
  'blog.goalpost-mcp-app.summary': 'Goalpost is now an MCP app: the real document, rendered beside your chat with Claude or ChatGPT, edited by you and your assistant at once.',
  'blog.webmcp.title': 'Your agent can now talk to Goalpost on the web',
  'blog.webmcp.summary': 'Goalpost’s editor now supports WebMCP, so an AI agent browsing alongside you can edit your document with you, right on the page.',
  // Legal pages (Privacy.tsx, Terms.tsx, LegalPage.tsx).
  'legal.eyebrow': 'Legal',
  'legal.effective': 'Effective {$date}',
  'privacy.title': 'Privacy Policy',
  'terms.title': 'Terms of Service',
  // Home page (Home.tsx). The closing CTA heading is split around an `<em>`
  // to preserve the inline emphasis. Paragraphs with two or more inline
  // emphasis tags drop the typography rather than fragment the catalog
  // (translators can re-add emphasis in the rendered string later).
  'home.document-title': 'Shared notes for people and agents',
  // The hero is a brand lockup: the logo mark and the "Goalpost" wordmark are
  // brand chrome (not translated, not headings), and this tagline is the `h1`.
  // It names what Goalpost is in plain words — a shared place to write things
  // down — and who it is for. It stands alone: the about box below says the
  // rest.
  'home.hero.title': 'Shared notes for people and agents.',
  // The page's one box (Home.tsx): what Goalpost is, in plain terms, beside a
  // screenshot of it. Informational rather than persuasive — it describes the
  // tool and links into the reference docs. The hero lede remains the page's
  // one line of pitch.
  'home.about.heading': 'About Goalpost',
  'home.about.body-1': 'Goalpost is a space for people and machines to share notes, context, reference materials, requirements, and decisions. Each document is a structured list of lists holding all of your detail, with sections and labels to keep things organized.',
  'home.about.body-2': 'You or your agents can edit your Goalpost document directly on the web, leverage our MCP to interact with it via chat, or switch off between the two when using ChatGPT, Claude Desktop, or Chrome’s built-in Gemini tab.',
  'home.about.body-3': 'Edits are collected into versions, each with a list of what changed, and the people on a document are emailed when a new version lands. Teams and approval rules are there for documents that want a review step.',
  'home.about.link.getting-started': 'Getting started →',
  'home.about.link.documents': 'Working with Documents →',
  'home.about.link.ai': 'AI Integration →',
  'home.about.image-alt': 'The Goalpost app’s document mid-edit: a tree of sections and notes with a new note being added under a section',
  // The agent demo clip that replaces the screenshot once synced: a screen
  // recording of an agent in Claude Desktop creating a document and drafting
  // its document through Goalpost's MCP server.
  'home.about.video-label': 'A screen recording of an AI agent in Claude Desktop setting up a Goalpost document and drafting its contents over MCP, with the result shown in the Goalpost app',
  // The second box: the feature list, each a name and one plain sentence.
  'home.features.heading': 'Features',
  'home.features.nested.title': 'Stores your notes in nested lists',
  'home.features.nested.body': 'Sections hold notes, notes hold more notes. Add links and images alongside them, and reference one note from another.',
  'home.features.labels.title': 'Labels',
  'home.features.labels.body': 'Tag notes with your own labels and filter the document by them.',
  'home.features.search.title': 'Search',
  'home.features.search.body': 'Find anything in a document by its text.',
  'home.features.notifications.title': 'Notifications',
  'home.features.notifications.body': 'The people on a document are emailed when a new version lands, with the list of what changed.',
  'home.features.approvals.title': 'Optional approvals for teams',
  'home.features.approvals.body': 'Ask for sign-off from a team before a version lands, on a whole document or just one section of it. Off by default.',
  // Pricing page (Pricing.tsx) + tier/feature data (pricing-tiers.ts).
  // The tier `key` identifiers ('free' / 'pro') remain English in code;
  // every user-visible label and positioning blurb resolves here.
  'pricing.document-title': 'Pricing',
  'pricing.hero.eyebrow': 'Pricing',
  'pricing.hero.title': 'Right-sized for you and your teams.',
  'pricing.hero.lede': 'Start free for solo work; go Pro when you need more documents and more members. Teams, approval rules, audit trail, version history, and AI assistant access come with both tiers.',
  'pricing.note': "Pro is billed per workspace member, monthly or yearly. Sign up free and upgrade from your workspace's Subscription page when you need to — your work moves with you.",
  'pricing.comparison.heading': 'What you get on each tier',
  'pricing.comparison.lede': 'Most of Goalpost is the same regardless of tier. The differences are document count and member count.',
  'pricing.comparison.aria': 'Tier feature comparison',
  'pricing.comparison.column.feature': 'Feature',
  'pricing.comparison.column.free': 'Free',
  'pricing.comparison.column.pro': 'Pro',
  'pricing.comparison.cell.included-aria': 'Included',
  'pricing.comparison.cell.not-included-aria': 'Not included',
  'pricing.cta.heading': 'Start on Free.',
  'pricing.cta.lede': 'Upgrade to Pro from inside the product when your work outgrows the free tier.',
  'pricing.tier.free.name': 'Free',
  'pricing.tier.free.price': '$0',
  'pricing.tier.free.positioning': 'For solo work or a small team with a small document.',
  'pricing.tier.free.cta': 'Sign up free',
  'pricing.tier.pro.name': 'Pro',
  'pricing.tier.pro.price-unit': 'per member / month',
  'pricing.tier.pro.price-yearly-note': 'or {$price} / member / month billed yearly',
  'pricing.tier.pro.price-unavailable': 'See pricing after signing up',
  'pricing.tier.pro.positioning': 'For studios, consultancies, and cross-functional teams managing many documents.',
  'pricing.tier.pro.cta': 'Sign up free, upgrade in-app',
  'pricing.feature.documents.label': 'Documents per workspace',
  'pricing.feature.documents.free': 'Up to 2',
  'pricing.feature.documents.pro': 'Up to 100',
  'pricing.feature.members.label': 'Members per workspace',
  'pricing.feature.members.free': 'Up to 3',
  'pricing.feature.members.pro': 'Unlimited',
  'pricing.feature.members.detail': 'Pro is billed per seat — add as many collaborators as the work needs.',
  'pricing.feature.teams.label': 'Teams and per-team approval rules',
  'pricing.feature.rules.label': 'All approval-rule types',
  'pricing.feature.rules.detail': 'None, all, majority, and at-least-N — on every tier.',
  'pricing.feature.audit.label': 'Full audit trail and version history',
  'pricing.feature.ai.label': 'AI assistant integration via MCP',
  'pricing.feature.ai.detail': 'Connect Claude, Cursor, and other MCP-compatible clients.',
  // Features → Documents (features/Documents.tsx). Decorative aria-hidden
  // mock content (filenames, fictional team names, version tags) stays
  // hardcoded as visual stage-dressing — those strings represent the
  // product, not user-facing UI copy.
  'features-documents.document-title': 'Working with Documents',
  'features-documents.hero.eyebrow': 'Documents',
  'features-documents.hero.title': 'Changes are first-class.',
  'features-documents.hero.lede': 'A Goalpost document is a living document of what you’re building. Edit it in drafts, save each batch of edits as a version, and Goalpost assembles the current document from every version so far — with a full history of how it got there.',
  'features-documents.hero.cta-start': 'Start a document',
  'features-documents.drafts.eyebrow': 'Drafts',
  'features-documents.drafts.heading': 'Edit requirements. Goalpost records the change.',
  'features-documents.drafts.body-1': 'Every edit you make lives inside a change batch — a named, reviewable collection of operations against the spec. There are only three: ADD a new requirement, REMOVE one that no longer fits, or REPLACE one with something better.',
  'features-documents.drafts.body-2': 'Drafts are free to experiment with. Multiple drafts can coexist off the same parent version. Nothing is silently overwritten.',
  'features-documents.lifecycle.eyebrow': 'Lifecycle',
  'features-documents.lifecycle.heading': 'Draft → Pending Approval → Approved.',
  'features-documents.lifecycle.lede': 'A change batch moves through three states. Each transition is explicit, recorded, and reversible up to the point of approval.',
  'features-documents.lifecycle.draft.label': 'Draft',
  'features-documents.lifecycle.draft.title': 'Safe to experiment',
  'features-documents.lifecycle.draft.body': 'Add, remove, and replace details freely. Multiple drafts can live alongside each other. Nothing is approved until you submit.',
  'features-documents.lifecycle.pending.label': 'Pending',
  'features-documents.lifecycle.pending.title': 'Awaiting sign-off',
  'features-documents.lifecycle.pending.body': "Submit the draft and Goalpost evaluates your document's approval rule in real time. Reviewers see the change — not just a reshuffled document.",
  'features-documents.lifecycle.approved.label': 'Approved',
  'features-documents.lifecycle.approved.title': 'Permanent history',
  'features-documents.lifecycle.approved.body': 'Once the rule is satisfied, the version is approved and joins the audit trail. Author, approvers, and the rules that governed it all captured at the moment of approval.',
  'features-documents.spec.eyebrow': 'Current spec',
  'features-documents.spec.heading': 'The spec stays current — automatically.',
  'features-documents.spec.body-1': 'You never edit a file called requirements_FINAL_v3.docx. Goalpost assembles the current specification from every change batch approved to date. It’s always the authoritative answer to “what does the document say today?”',
  'features-documents.spec.body-2': "Need last month's view? Every approved version is addressable. Time-travel to any prior state without losing the history that got you here.",
  'features-documents.details.eyebrow': 'Details',
  'features-documents.details.heading': 'Broad intent at the top. Then refine, refine, refine.',
  'features-documents.details.lede': 'Specs aren’t flat lists. Goalpost organizes content as a tree of details — so you can start with “Living Room” and refine down to “Paint · Walls · Benjamin Moore OC-17” as the document matures.',
  'features-documents.workspace.eyebrow': 'Workspace',
  'features-documents.workspace.heading': 'Many documents. One workspace.',
  'features-documents.workspace.lede': "Each document carries its own stakeholders, approval rules, and history. Need any one person to sign off technical requirements for your team and unanimity for your office decor document with your partner? We've got you covered.",
  'features-documents.workspace.footnote': 'Configure rules per document — or skip approvals entirely for your own work.',
  'features-documents.crosslink.approvals.desc': 'Encode exactly who must sign off and how. Teams + document rules compose into one evaluation.',
  'features-documents.crosslink.ai.desc': 'Let MCP-compatible assistants read your document and draft changes against it, in the same document your team works in.',
  'features-documents.cta.heading': 'Ready to try it?',
  'features-documents.cta.lede': 'Start a document in minutes. Free tier, no credit card.',
  // Features → Approvals (features/Approvals.tsx).
  'features-approvals.document-title': 'Approval Rules',
  'features-approvals.hero.eyebrow': 'Approvals',
  'features-approvals.hero.title': 'Define approval your way.',
  'features-approvals.hero.lede': 'Approval rules encode who must sign off before a change becomes official — named once, composed from your teams, and scoped to exactly what each change touches.',
  'features-approvals.hero.cta-try': 'Try it free',
  'features-approvals.overview.eyebrow': 'Overview',
  'features-approvals.overview.heading': 'How approval rules work.',
  'features-approvals.overview.lede': 'Build a rule from your teams, apply it to a document, and scope it to the sections that need extra sign-off — then watch it evaluate as approvals land.',
  'features-approvals.overview.video-label': 'A walkthrough of setting up and applying an approval rule in Goalpost',
  'features-approvals.named-rules.eyebrow': 'Named, composable rules',
  'features-approvals.named-rules.heading': 'Write a rule once. Reuse it everywhere.',
  'features-approvals.named-rules.body-1': 'An approval rule is a named policy you author once in your workspace and apply to any document. A rule is a set of conditions joined by “and” — every condition must be met. Within a condition you can list alternatives joined by “or,” so any one of them satisfies it.',
  'features-approvals.named-rules.body-2': 'A condition can name a team or another rule, so you compose larger policies out of smaller ones instead of rebuilding them. Edit a rule in one place and every document and section that uses it picks up the change.',
  'features-approvals.named-rules.docs-link': 'Read the full model in the Approval Rules guide →',
  'features-approvals.named-rules.expr-eyebrow': 'Named rule',
  'features-approvals.named-rules.expr-note': '“Design review” is itself a named rule — composed once and reused by reference, not re-listed.',
  'features-approvals.named-rules.or-eyebrow': 'Or-alternative',
  'features-approvals.named-rules.or-note': 'A single condition with two alternatives: either engineering team can cover a change to shared infrastructure.',
  'features-approvals.team-quorum.eyebrow': 'Team-level quorum',
  'features-approvals.team-quorum.heading': 'Each team decides what “approved” means for it.',
  'features-approvals.team-quorum.lede': 'A rule names which teams must sign off; how a given team signs off is the team’s own setting — any one member, every member, or a set number of them. Counting lives at the team level, so rules above it simply name the teams.',
  'features-approvals.scoped.eyebrow': 'Scoped approval',
  'features-approvals.scoped.heading': 'Approval accumulates by what a change touches.',
  'features-approvals.scoped.lede': 'A document can set a baseline rule, and any section can carry its own. A change has to satisfy only the rules that govern what it actually edits — never more teams than the change affects, and never fewer.',
  'features-approvals.scoped.change-a.tag': 'Change A',
  'features-approvals.scoped.change-a.what': 'Edits Growth → Welcome Tour copy',
  'features-approvals.scoped.change-a.foot': 'One matched rule. Tom and Gina sign off and the change is done — no leadership, no design.',
  'features-approvals.scoped.change-b.tag': 'Change B',
  'features-approvals.scoped.change-b.what': 'Edits the tour copy and its Visual Design',
  'features-approvals.scoped.change-b.foot': 'The design detail pulls in Design review on top of Growth review, so the App Design Team signs off too. The Growth rule never changed.',
  'features-approvals.scoped.why-tag': 'Required by an edited section',
  'features-approvals.realtime.eyebrow': 'Where things stand',
  'features-approvals.realtime.heading': 'See exactly where a change stands.',
  'features-approvals.realtime.body-1': 'At a glance, reviewers see which teams have signed off, which are still reviewing, and who specifically is blocking. No more “did eng approve this yet?” threads.',
  'features-approvals.realtime.body-2': 'Every requirement shows why it applies — the rule it came from, and whether an edited section pulled it in. When all required approvals are in, the change approves itself and everyone is notified.',
  'features-approvals.lifecycle.eyebrow': 'Lifecycle',
  'features-approvals.lifecycle.heading': 'Submit. Approve. Or push back.',
  'features-approvals.lifecycle.submit.label': 'Submit',
  'features-approvals.lifecycle.submit.title': 'Move draft to pending',
  'features-approvals.lifecycle.submit.body': 'The author flips a draft into review. Goalpost evaluates the matched rules at once — if nothing governed is touched, it auto-approves.',
  'features-approvals.lifecycle.approve.label': 'Approve',
  'features-approvals.lifecycle.approve.title': 'Sign off on behalf of a team',
  'features-approvals.lifecycle.approve.body': 'Each approval is recorded against its team. Goalpost re-evaluates in real time and approves the moment every matched rule is satisfied.',
  'features-approvals.lifecycle.reject.label': 'Reject / Withdraw',
  'features-approvals.lifecycle.reject.title': 'Pull it back for rework',
  'features-approvals.lifecycle.reject.body': 'A member of a required team can reject; the author can withdraw. Either returns the change to draft and clears pending approvals — and the timeline keeps the whole loop.',
  'features-approvals.audit.eyebrow': 'Audit trail',
  'features-approvals.audit.heading': 'The rule that approved it, captured forever.',
  'features-approvals.audit.lede': 'At the moment of approval, Goalpost freezes the fully-resolved rule that governed the change — every team, its quorum, and who signed off. A separate append-only timeline records each submit, approve, reject, and withdraw, so rework stays visible.',
  'features-approvals.audit.body': 'Rename a team or rewrite a rule later and the record still shows the policy that was actually in force. You can even add an external reviewer as a team with no approval authority, so an auditor reads the full history without being able to change anything.',
  'features-approvals.docs.eyebrow': 'Reference',
  'features-approvals.docs.heading': 'The complete approval model.',
  'features-approvals.docs.body': 'Baselines, section-carried rules, the matched rule set, and the full audit trail — documented end to end.',
  'features-approvals.docs.cta': 'Read the Approval Rules guide',
  'features-approvals.crosslink.documents.desc': 'Change Batches, drafts, diffs, and how approved versions assemble into the current spec.',
  'features-approvals.crosslink.ai.desc': 'AI can author change batches but can’t vote — approvals stay human-gated by design.',
  'features-approvals.cta.heading': 'Encode your sign-off rules.',
  'features-approvals.cta.lede': 'Stop losing consensus to chat scroll-back.',
  // Features → AI (features/AI.tsx).
  'features-ai.document-title': 'AI Integration',
  'features-ai.hero.eyebrow': 'AI Integration',
  'features-ai.hero.title': 'Your agent works from the same notes.',
  'features-ai.hero.lede': 'Connect Claude, Cursor, or any MCP-compatible assistant to your Goalpost workspace. It reads the current document, drafts changes against it, and builds from what’s written down instead of guessing. Its edits land in the same document your team works in.',
  'features-ai.hero.cta-try': 'Try AI drafting',
  // The agent demo clip, shown between the hero and the walkthrough once the
  // media manifest carries it (the section is absent until then).
  'features-ai.demo.eyebrow': 'In action',
  'features-ai.demo.heading': 'One prompt, one document.',
  'features-ai.demo.lede': 'An agent in Claude Desktop sets up a document, drafts its contents, and saves the first version — all through Goalpost’s MCP server, with the result in the app beside it.',
  'features-ai.demo.video-label': 'A screen recording of an AI agent in Claude Desktop setting up a Goalpost document and drafting its contents over MCP, with the result shown in the Goalpost app',
  'features-ai.bot-to-spec.eyebrow': 'From Bot to Spec',
  'features-ai.bot-to-spec.heading': 'Describe the change. Review the draft. Submit.',
  'features-ai.bot-to-spec.body-1': "Paste meeting notes, dictate the change, or let the AI propose edits based on what's already in the spec. Goalpost validates every operation — parent references, detail hierarchy, required fields — before anything touches your document.",
  'features-ai.bot-to-spec.body-2': 'The output is a normal draft change batch. Review it, edit if needed, and submit it through the same approval flow as any other change.',
  'features-ai.bot-to-spec.docs-link': 'See how to connect your assistant →',
  'features-ai.spec-to-bot.eyebrow': 'From Spec to Bot',
  'features-ai.spec-to-bot.heading': "Your spec is the AI's context.",
  'features-ai.spec-to-bot.lede': "Drafting change batches is the headline, but the same connection works in the other direction — every downstream task gets better when AI can read what you've agreed on, instead of guessing.",
  'features-ai.spec-to-bot.code.label': 'Code',
  'features-ai.spec-to-bot.code.title': 'Generate implementations',
  'features-ai.spec-to-bot.code.body': 'Ask for the API handlers, the database schema, or the test cases for a feature. The AI works from the approved spec, not a guess at what you meant.',
  'features-ai.spec-to-bot.copy.label': 'Copy',
  'features-ai.spec-to-bot.copy.title': 'Draft communications',
  'features-ai.spec-to-bot.copy.body': 'Release notes, changelogs, status updates, support replies — drawn directly from the change batches that actually shipped.',
  'features-ai.spec-to-bot.qa.label': 'Q&A',
  'features-ai.spec-to-bot.qa.title': 'Answer questions',
  'features-ai.spec-to-bot.qa.body': '“What does our spec say about X?” — with Goalpost as the source of truth, the AI gives you the current answer, not a stale one.',
  'features-ai.boundaries.eyebrow': 'Boundaries',
  'features-ai.boundaries.heading': "What AI can do. What it can't.",
  'features-ai.boundaries.lede': 'AI is a research partner and first-draft author. It never bypasses the humans in your governance model.',
  'features-ai.boundaries.can.heading': 'AI can',
  'features-ai.boundaries.can.1': "Read your document's current spec and history",
  'features-ai.boundaries.can.2': 'Inspect pending change batches and their status',
  'features-ai.boundaries.can.3': 'Create draft change batches with any combination of ADD, REMOVE, and REPLACE operations',
  'features-ai.boundaries.can.4': 'Submit drafts for approval on your behalf',
  'features-ai.boundaries.can.5': 'Work across the full detail tree, at any depth',
  'features-ai.boundaries.cannot.heading': "AI can't",
  'features-ai.boundaries.cannot.1': 'Cast approval votes on any team',
  'features-ai.boundaries.cannot.2': "Reject or withdraw other people's change batches",
  'features-ai.boundaries.cannot.3': 'Create teams, add members, or edit approval rules',
  'features-ai.boundaries.cannot.4': 'Touch billing, subscription, or workspace settings',
  'features-ai.boundaries.cannot.5': 'Act outside the workspace you authorized it for',
  'features-ai.governance.eyebrow': 'Governance',
  'features-ai.governance.heading': 'AI drafts go through your normal approval flow.',
  'features-ai.governance.lede': "There's no special “AI bypass” for approvals. A change batch authored by an AI assistant is indistinguishable from one authored by a human — it needs the same sign-off from the same teams under the same rule.",
  'features-ai.governance.auth.label': 'Auth',
  'features-ai.governance.auth.title': 'Workspace-pinned tokens',
  'features-ai.governance.auth.body': "Each MCP authorization binds to a single Goalpost workspace. Multi-workspace users run the flow once per workspace — so an AI can't accidentally write to the wrong org.",
  'features-ai.governance.audit.label': 'Audit',
  'features-ai.governance.audit.title': 'Every action traceable',
  'features-ai.governance.audit.body': 'Every draft, submission, and API call is logged with the token identity that made it. External reviewers see the same audit trail as everyone else.',
  'features-ai.governance.validation.label': 'Validation',
  'features-ai.governance.validation.title': 'Malformed edits never get approved',
  'features-ai.governance.validation.body': 'Goalpost validates every operation — IDs, parent references, hierarchy constraints — before persisting. An AI that gets confused produces a clear error, not corrupt data.',
  'features-ai.availability.eyebrow': 'Availability',
  'features-ai.availability.heading': 'Included with every plan.',
  'features-ai.availability.lede': 'MCP access comes with every workspace, Free and Pro alike. Connect an assistant the day you sign up.',
  'features-ai.docs.eyebrow': 'Setup',
  'features-ai.docs.heading': 'Connect your assistant in minutes.',
  'features-ai.docs.body': "One address, OAuth sign-in, and you're drafting. The connection guide walks through Claude Desktop, Claude Code, ChatGPT, Cursor, and any other MCP-compatible assistant.",
  'features-ai.docs.cta': 'Read the AI connection guide',
  'features-ai.crosslink.documents.desc': 'What AI writes to: change batches, drafts, and the spec that assembles itself from every approval.',
  'features-ai.crosslink.approvals.desc': 'The governance model AI drafts flow through. No bypass — humans stay the authority.',
  'features-ai.cta.heading': 'Give your agent something to work from.',
  'features-ai.cta.lede': 'And a place to write back.',
  'demo.document-title': 'Product demo',
  'demo.hero.eyebrow': 'Product demo',
  'demo.hero.title': 'See Goalpost in action.',
  'demo.hero.lede': 'A complete guided tour in about eight minutes. Watch a product team draft changes, collect approvals, and keep one official record, then explore the teams and rules that make it work.',
  'demo.hero.cta-try': 'Try it free',
  'demo.player.video-label': 'The full Goalpost product demo: a chaptered walkthrough of documents, details, change batches, approvals, teams, approval rules, and AI integration',
  'demo.player.chapters-heading': 'Chapters',
  'demo.player.chapter-welcome': 'Welcome',
  'demo.cta.heading': 'Ready to give it a try?',
  'demo.cta.lede': 'Create your workspace today.',
  // Notifications page (Notifications.tsx) — finishes the partial
  // migration. Per-row aria labels are parameterized on the document
  // name so screen-reader output stays specific.
  'notifications.document-title': 'Notifications',
  'notifications.description': 'Choose which document events send you an email. These preferences apply only to you — every document member manages their own.',
  'notifications.fetch-error': 'Failed to load notification preferences',
  'notifications.save-error': 'Failed to update preference',
  'notifications.empty': 'You are not a member of any documents yet, so there are no notifications to manage.',
  'notifications.column.document': 'Document',
  'notifications.column.change-batch-approved': 'Change batch approved',
  'notifications.column.approval-submitted': 'Submitted for approval',
  'notifications.column.partial-approval': 'Approval received',
  'notifications.column.change-batch-rejected': 'Change batch rejected',
  'notifications.row.change-batch-approved.aria': 'Email me when a change batch is approved in {$document}',
  'notifications.row.approval-submitted.aria': 'Email me when a change batch is submitted for approval in {$document}',
  'notifications.row.partial-approval.aria': 'Email me when my change batch gets a partial approval in {$document}',
  'notifications.row.change-batch-rejected.aria': 'Email me when my change batch is rejected back to draft in {$document}',
};

export default bundle;
